// src/Pages/Etusivu/Etusivu.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { PlusCircle, MinusCircle, X } from "lucide-react";
import AOS from "aos";
import { api } from "../../services/api";
import WebDesignBanner from "../../components/HeaderWithAd/HeaderWithAd.jsx";
// 🎥 Hero-videot (LIGHT / DARK) — vaihda nimet/polut omiin tiedostoihin
import heroVideoLight from "../../assets/front-page-light.mp4";
import heroVideoDark from "../../assets/front-page.mp4";

// 🖼️ Posterit (valinnainen: voit käyttää myös yhtä samaa posteria molemmille)
import heroPosterLight from "../../assets/front-page-light-poster.webp";
import heroPosterDark from "../../assets/front-page-dark-poster.webp";
import {
  trackEvent,
  trackDateSelected,
  trackArtistTypeSelected,
  trackRegionSelected,
  trackProfileOpened,
  trackBookingModalOpened
} from "../../utils/analytics";
import { ARTIST_TYPES } from "../../config/artistTypes";

import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import ArtistCard from "../../components/Esiintyjakortti/Esiintyjakortti";
import CompanyCard from "../../components/Yrityskortti/Yrityskortti.jsx";
import { motion, AnimatePresence } from "framer-motion";
import { slugParamToNames, namesToSlugList } from "../../utils/fiRegion";
import municipalitiesFI from "../../data/municipalities.fi.json";

// 🧩 Overlay-spinneri (pyörivä logo + blur-tausta)
import { PageOverlayLoading } from "../../components/Loading/Spinner.jsx";
// 🔧 VAIHDA TÄHÄN oma taustakuva (tai poista ja käytä pelkkää gradienttia)
import heroBg from "../../assets/backstage_dark.webp";
// Logoassetit suoraan ESM-importeilla (älä käytä require() Viten kanssa)
import logoLight from "../../assets/logo-buukkeri-dark.webp";
import logoDark from "../../assets/logo-buukkeri-dark.webp";

const BookingModal = lazy(() => import("../../components/BookingModal/BookingModal"));
const BookingFlow = lazy(() => import("../../components/Bookkausneuvo/Bookkausneuvo"));
const LatestArtists = lazy(() => import("../../components/LatestArtists/LatestArtists.jsx"));
const FeaturedProfileCard = lazy(() =>
  import("../../components/FeaturedProfileCard/FeaturedProfileCard.jsx")
);
const MonthlyFeatured = lazy(() => import("../../components/MonthlyFeatured/MonthlyFeatured"));
const WhyBuukkeriSection = lazy(() =>
  import("../../components/WhyBuukkeriSection/WhyBuukkeriSection")
);
const HomeFAQ = lazy(() => import("../../components/HomeFAQ/HomeFAQ"));

const LazySectionFallback = () => (
  <div className="min-h-[160px]" aria-hidden="true" />
);

const BOOT_MIN_SHOW_MS = 900;    // kevyempi ensimaalauksen minimiaika
const BOOT_FAILSAFE_MS = 5000;   // nopeampi hätäkatkaisu
const HOME_BOOT_KEY    = "bbx:homeBootShown"; // overlay vain kerran / selain-välilehti
const HOME_EMBED_TOAST_KEY = "bbx:homeEmbedToastDismissed";
const HOME_EMBED_TOAST_DELAY_MS = 900;
const HOME_EMBED_TOAST_HIDE_MS = 7000;
// Etusivun palveluntarjoajille suunnattu Embed-toast.
// Pidetään koodi tallessa, mutta piilotetaan buukkaajaliikenteen ajaksi.
// Palauta näkyviin vaihtamalla trueksi.
const HOME_EMBED_TOAST_ENABLED = false;
const BELOW_FOLD_DEFER_MS = 900; // alemmat etusivun osiot mountataan kevyellä viiveellä

// Älä näytä overlayta devissä/localhostissa
const IS_DEV =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.NODE_ENV !== "production") ||
  (typeof window !== "undefined" && window?.location?.hostname === "localhost");

// Älä näytä boot-overlayta mobiilissa tuotannossa
const isMobileViewport = () => {
  try {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  } catch {
    return false;
  }
};

// Suppressoi overlayn jos tultiin sivuston sisältä (esim. /blogi → /)
function hasInternalReferrer() {
  try {
    if (typeof document === "undefined") return false;
    const ref = document.referrer || "";
    if (!ref) return false; // tyhjä referrer = ensimmäinen tulo tai referrer blokattu
    const curOrigin = window.location.origin;
    const refOrigin = new URL(ref).origin;
    return refOrigin === curOrigin; // sama origin → sisäinen navigaatio
  } catch {
    return false;
  }
}

// ---------------------------------------------------------
// FEATURE-FLAG: näytetäänkö yritykset etusivulla?
// Palauta yritykset vaihtamalla arvoon true.
// ---------------------------------------------------------
const SHOW_COMPANIES = false;

// ---------------- Consts ----------------
const RESULTS_ID = "tulokset";
const DATE_KEY = "buukkeri:selecteddate";
const HOME_FILTERS_KEY = "buukkeri:homeFilters:v1";
const HOME_AI_RETURN_KEY = "buukkeri:homeAiReturn:v1";

// Muista valitut maakunnat jos URL:ia ei ole
const REGIONS_KEY = "buukkeri:selectedregions";
const QUICK_REGIONS = ["Uusimaa", "Pirkanmaa", "Varsinais-Suomi", "Päijät-Häme"];

// ---------------------------------------------------------
// SEO / sisäiset linkit: tärkeimmät ländärit etusivun tulosalueelle
// - Ei heroon, jotta mobiilin ensinäkymä pysyy kevyenä.
// - Näytetään ensin tärkeimmät, loput avautuvat napilla.
// - Lista perustuu App.jsx:n olemassa oleviin React-ländärireitteihin.
// ---------------------------------------------------------
  const LANDING_PRICE_LINKS_PRIMARY = [
    { href: "/esiintyjat-ja-palvelut/", label: "Kaikki esiintyjät ja palvelut" },

    // GSC:n vahvimmat valtakunnalliset haut
    { href: "/standup-koomikko-juhliin/", label: "Stand up -koomikko juhliin" },
    { href: "/juontaja-juhliin/", label: "Juontaja juhliin" },
    { href: "/trubaduuri-juhliin/", label: "Trubaduuri juhliin" },
    { href: "/bilebandi-juhliin/", label: "Bilebändi juhliin" },
    { href: "/valokuvaaja-juhliin/", label: "Valokuvaaja juhliin" },
    { href: "/duo-juhliin/", label: "Duo juhliin" },
    { href: "/taikuri-juhliin/", label: "Taikuri juhliin" },

    // GSC:n vahvimmat alueelliset länderit
    { href: "/trubaduuri-pirkanmaa/", label: "Trubaduuri Pirkanmaa" },
    { href: "/trubaduuri-helsinki/", label: "Trubaduuri Helsinki" },
    { href: "/trubaduuri-uusimaa/", label: "Trubaduuri Uusimaa" },
    { href: "/bilebandi-espoo/", label: "Bilebändi Espoo" },
    { href: "/bilebandi-varsinais-suomi/", label: "Bilebändi Varsinais-Suomi" },
    { href: "/trubaduuri-paijat-hame/", label: "Trubaduuri Päijät-Häme" },

    // Uusi länderi, jolle annetaan heti vahva sisäinen linkki
    { href: "/trubaduuri-oulu/", label: "Trubaduuri Oulu" },
  ];

const LANDING_PRICE_LINKS_MORE = [
  // Koko Suomi
  { href: "/duo-juhliin/", label: "Duo juhliin" },
  { href: "/sooloartisti-juhliin/", label: "Sooloartisti juhliin" },
  { href: "/taikuri-juhliin/", label: "Taikuri juhliin" },

  // Uusimaa
  { href: "/bilebandi-uusimaa/", label: "Bilebändi Uusimaa" },
  { href: "/bilebandi-helsinki/", label: "Bilebändi Helsinki" },
  { href: "/bilebandi-vantaa/", label: "Bilebändi Vantaa" },
  { href: "/bilebandi-kerava/", label: "Bilebändi Kerava" },
  { href: "/bilebandi-jarvenpaa/", label: "Bilebändi Järvenpää" },
  { href: "/bilebandi-tuusula/", label: "Bilebändi Tuusula" },
  { href: "/dj-uusimaa/", label: "DJ Uusimaa" },
  { href: "/duo-uusimaa/", label: "Duo Uusimaa" },
  { href: "/duo-helsinki/", label: "Duo Helsinki" },
  { href: "/valokuvaaja-uusimaa/", label: "Valokuvaaja Uusimaa" },
  { href: "/sooloartisti-uusimaa/", label: "Sooloartisti Uusimaa" },
  { href: "/taikuri-uusimaa/", label: "Taikuri Uusimaa" },
  { href: "/taikuri-helsinki/", label: "Taikuri Helsinki" },

  // Pirkanmaa
  { href: "/bilebandi-pirkanmaa/", label: "Bilebändi Pirkanmaa" },
  { href: "/dj-pirkanmaa/", label: "DJ Pirkanmaa" },
  { href: "/trubaduuri-pirkanmaa/", label: "Trubaduuri Pirkanmaa" },
  { href: "/trubaduuri-tampere/", label: "Trubaduuri Tampere" },
  { href: "/duo-pirkanmaa/", label: "Duo Pirkanmaa" },
  { href: "/valokuvaaja-pirkanmaa/", label: "Valokuvaaja Pirkanmaa" },
  { href: "/sooloartisti-pirkanmaa/", label: "Sooloartisti Pirkanmaa" },
  { href: "/taikuri-pirkanmaa/", label: "Taikuri Pirkanmaa" },

  // Päijät-Häme
  { href: "/bilebandi-paijat-hame/", label: "Bilebändi Päijät-Häme" },
  { href: "/dj-paijat-hame/", label: "DJ Päijät-Häme" },
  { href: "/trubaduuri-paijat-hame/", label: "Trubaduuri Päijät-Häme" },
  { href: "/duo-paijat-hame/", label: "Duo Päijät-Häme" },
  { href: "/sooloartisti-paijat-hame/", label: "Sooloartisti Päijät-Häme" },
  { href: "/taikuri-paijat-hame/", label: "Taikuri Päijät-Häme" },

  // Varsinais-Suomi
  { href: "/bilebandi-varsinais-suomi/", label: "Bilebändi Varsinais-Suomi" },
  { href: "/dj-varsinais-suomi/", label: "DJ Varsinais-Suomi" },
  { href: "/trubaduuri-varsinais-suomi/", label: "Trubaduuri Varsinais-Suomi" },
  { href: "/trubaduuri-turku/", label: "Trubaduuri Turku" },
  { href: "/duo-varsinais-suomi/", label: "Duo Varsinais-Suomi" },
  { href: "/valokuvaaja-varsinais-suomi/", label: "Valokuvaaja Varsinais-Suomi" },
  { href: "/sooloartisti-varsinais-suomi/", label: "Sooloartisti Varsinais-Suomi" },
  { href: "/taikuri-varsinais-suomi/", label: "Taikuri Varsinais-Suomi" },

  // Kanta-Häme
  { href: "/duo-kanta-hame/", label: "Duo Kanta-Häme" },
  { href: "/valokuvaaja-kanta-hame/", label: "Valokuvaaja Kanta-Häme" },
  { href: "/sooloartisti-kanta-hame/", label: "Sooloartisti Kanta-Häme" },
  { href: "/taikuri-kanta-hame/", label: "Taikuri Kanta-Häme" },

  // Satakunta
  { href: "/duo-satakunta/", label: "Duo Satakunta" },
  { href: "/sooloartisti-satakunta/", label: "Sooloartisti Satakunta" },
  { href: "/taikuri-satakunta/", label: "Taikuri Satakunta" },

  // Kymenlaakso
  { href: "/duo-kymenlaakso/", label: "Duo Kymenlaakso" },
  { href: "/sooloartisti-kymenlaakso/", label: "Sooloartisti Kymenlaakso" },

   // Oulu / Pohjois-Pohjanmaa
  { href: "/bilebandi-oulu/", label: "Bilebändi Oulu" },
  { href: "/dj-oulu/", label: "DJ Oulu" },
  { href: "/duo-oulu/", label: "Duo Oulu" },
  { href: "/valokuvaaja-oulu/", label: "Valokuvaaja Oulu" },
  { href: "/taikuri-oulu/", label: "Taikuri Oulu" },
  { href: "/juontaja-oulu/", label: "Juontaja Oulu" },
  { href: "/standup-koomikko-oulu/", label: "Stand up -koomikko Oulu" },
  { href: "/sooloartisti-oulu/", label: "Sooloartisti Oulu" },
];

// ---------------------------------------------------------
// ⚡ Desktop pikavalinnat (chipit)
// - pidä määrä pienenä, ettei UI:sta tule “chip-mattoa”
// ---------------------------------------------------------
const QUICK_ARTIST_TYPES = [
  "Bilebändi",
  "Duo",
  "Trubaduuri",
  "Taikuri",
  "Sirkus & show",
  "Valokuvaaja",
  "Juontaja",
  "Standup-koomikko",
];

const ARTIST_TYPE_ORDER = [
  "Bilebändi",
  "Trubaduuri",
  "Duo",
  "Taikuri",
  "Sirkus & show",
  "Valokuvaaja",
  "Sooloartisti",
  "DJ",
  "Juontaja",
  "Standup-koomikko",
  "Bändi (oma musiikki)",
];

const TYPE_FALLBACKS = {
  "Duo": ["Trubaduuri", "Bilebändi"],
  "Trubaduuri": ["Duo", "Bilebändi"],
  "Bilebändi": ["Duo", "Trubaduuri"],
  "Sooloartisti": ["Trubaduuri", "Duo"],
};

 // ---------------------------------------------------------
 // 💶 Budjetti (front-suodatin)
 // - bucketit linjassa backendin kanssa:
 //   <500, 500-799, 800-1199, 1200-1799, 1800-2499, 2500+
 // ---------------------------------------------------------
 const PRICE_BUCKET_OPTIONS = [
   { value: "",         label: "Kaikki" },
   { value: "<500",     label: "Alle 500 €" },
   { value: "500-799",  label: "500–799 €" },
   { value: "800-1199", label: "800–1199 €" },
   { value: "1200-1799",label: "1200–1799 €" },
   { value: "1800-2499",label: "1800–2499 €" },
   { value: "2500+",    label: "2500 €+" },
 ];

 const parseMoneyFi = (v) => {
   if (v == null) return null;
   const s = String(v).trim();
   if (!s) return null;
   const n = Number(s.replace(",", "."));
   return Number.isFinite(n) ? n : null;
 };

 const matchesPriceBucket = (startingPriceNumber, bucket) => {
   if (!bucket) return true;
   if (startingPriceNumber == null) return false; // jos budjettia suodatetaan, mutta hintaa ei ole → pois
   const sp = startingPriceNumber;
   if (bucket === "<500") return sp >= 0 && sp < 500;
   if (bucket === "500-799") return sp >= 500 && sp < 800;
   if (bucket === "800-1199") return sp >= 800 && sp < 1200;
   if (bucket === "1200-1799") return sp >= 1200 && sp < 1800;
   if (bucket === "1800-2499") return sp >= 1800 && sp < 2500;
   if (bucket === "2500+") return sp >= 2500;
   return true;
 };

// const baseArtistTypes = SHOW_COMPANIES
//   ? ["Bilebändi", "Trubaduuri", "Taikuri", "Duo", "Sooloartisti", "Valokuvaaja", "DJ", "Bändi (oma musiikki)", "Standup-koomikko", "Juontaja", "Yritys"]
//   : ["Bilebändi", "Trubaduuri", "Taikuri", "Duo", "Sooloartisti", "Valokuvaaja", "DJ", "Bändi (oma musiikki)", "Standup-koomikko", "Juontaja"];

// Musatyypit (määrittää näytetäänkö "Musiikkityyli"-suodatin vai ei)
const MUSIC_TYPE_KEYS = ["dj", "trub", "bilebänd", "bileband", "bänd", "band", "duo", "soolo"];

// URL-parametri → valikon arvo -kartta
const TYPE_MAP = {
  dj: "DJ",
  trubaduuri: "Trubaduuri",
  bilebandi: "Bilebändi",
  "bilebändi": "Bilebändi",
  bandi: "Bändi (oma musiikki)",
  "bändi": "Bändi (oma musiikki)",
  band: "Bändi (oma musiikki)",
  taikuri: "Taikuri",
  juontaja: "Juontaja",
  "standup-koomikko": "Standup-koomikko",
  sooloartisti: "Sooloartisti",
  valokuvaaja: "Valokuvaaja",
  duo: "Duo",
  "sirkus-show": "Sirkus & show",
  sirkus_show: "Sirkus & show",
  sirkus: "Sirkus & show",
  all: ""
};

// UI-arvo → URL-paramin arvo
const TYPE_TO_PARAM = {
  DJ: "dj",
  Trubaduuri: "trubaduuri",
  "Bilebändi": "bilebandi",
  "Bändi (oma musiikki)": "bandi",
  "Bändi": "bandi", // backwards-compat (vanha label / tallennetut arvot)
  Sooloartisti: "sooloartisti",
  Taikuri: "taikuri",
  Juontaja: "juontaja",
  "Standup-koomikko": "standup-koomikko",
  Valokuvaaja: "valokuvaaja",
  Duo: "duo",
  "Sirkus & show": "sirkus-show",
  Yritys: "yritys",
};

// Fallback: param → UI (uudet tyypit: taikuri, juontaja, standup-koomikko, jne.)
const typeParamToUi = (rawParam) => {
  const raw = String(rawParam || "").trim().toLowerCase();
  if (!raw) return "";
  const mapped = TYPE_MAP[raw];
  // mapped voi olla "" (all) tai string — jos löytyy avaimesta, palautetaan sellaisenaan
  if (mapped !== undefined) return mapped;
  // fallback: "standup-koomikko" -> "Standup koomikko"
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
};

// Fallback: UI → param (uudet tyypit)
const uiTypeToParam = (uiValue) => {
  const s = String(uiValue || "").trim();
  if (!s) return "";
  const mapped = TYPE_TO_PARAM[s];
  if (mapped) return mapped;
  // slugify: "Standup koomikko" -> "standup-koomikko"
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // poista aksentit (ä->a, ö->o)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Pikavalinnat genreille (täsmälleen sama kirjoitusasu kuin public.genres.name)
const QUICK_GENRES = [
  "Iskelmä",
  "Bilemusiikki",
  "80-luvun listahitit",
  "Ulkomainen rock",
];

// ------------------------------------------------------------
// ✅ MASTER: ohjelmat (näytetään aina valikossa tyypin mukaan)
// HUOM: value = backendin id/slug (band.additional_services[])
//       label = ihmisnimi UI:lle
// ------------------------------------------------------------
const PROGRAM_CATALOG = [
  // Taikuus
  { id: "magic-closeup", label: "Lähitaikuus / kiertävä pöytätaikuus", typeKey: "taikuri" },
  { id: "magic-stage", label: "Lavashow", typeKey: "taikuri" },
  { id: "magic-mentalism", label: "Mentalismi", typeKey: "taikuri" },
  { id: "magic-custom", label: "Räätälöity show (yritykset / teemat)", typeKey: "taikuri" },
  { id: "magic-kids", label: "Lasten taikashow", typeKey: "taikuri" },

  // Sirkus & show
  { id: "circus-acrobatics", label: "Akrobatia", typeKey: "sirkus" },
  { id: "circus-aerial", label: "Ilma-akrobatia", typeKey: "sirkus" },
  { id: "circus-character", label: "Hahmoesitys", typeKey: "sirkus" },
  { id: "circus-physical-comedy", label: "Fyysinen komedia", typeKey: "sirkus" },
  { id: "circus-dance", label: "Tanssi", typeKey: "sirkus" },
  { id: "circus-juggling", label: "Jongleeraus", typeKey: "sirkus" },
  { id: "circus-stilts", label: "Puujalat / stilttikävely", typeKey: "sirkus" },
  { id: "circus-fire-show", label: "Tulishow", typeKey: "sirkus" },
  { id: "circus-visual-show", label: "Visuaalinen show", typeKey: "sirkus" },
  { id: "circus-roaming", label: "Kiertävä / roaming-ohjelma", typeKey: "sirkus" },
  { id: "circus-icebreaker", label: "Jäänmurtaja", typeKey: "sirkus" },
  { id: "circus-guest-reception", label: "Vieraiden vastaanotto", typeKey: "sirkus" },
  { id: "circus-workshop", label: "Sirkustyöpaja", typeKey: "sirkus" },
];

const programOptionsForType = (rawType) => {
  const t = String(rawType || "").toLowerCase().trim();
  if (!t) return [];
  return PROGRAM_CATALOG
    .filter((p) => t.includes(p.typeKey))
    .map((p) => ({ value: p.id, label: p.label }))
    .sort((a, b) => a.label.localeCompare(b.label, "fi", { sensitivity: "base" }));
};

// Palauta tämän päivän pvm paikallisajassa muodossa YYYY-MM-DD
function todayLocalISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Poimi AI-hakulauseesta selkeä suomalainen päivämäärä, esim. 23.10 tai 23.10.2026.
// Jos vuotta ei anneta ja kyseinen päivä on jo mennyt, käytetään seuraavaa vuotta.
function extractExplicitDateFromAiQuery(query) {
  const text = String(query || "");
  const match = text.match(
    /(?:^|[^\d])(\d{1,2})\.(\d{1,2})(?:\.(\d{2}|\d{4}))?(?=$|[^\d])/
  );

  if (!match) return "";

  const day = Number(match[1]);
  const month = Number(match[2]);
  const rawYear = match[3] || "";

  if (
    !Number.isInteger(day) ||
    !Number.isInteger(month) ||
    day < 1 ||
    day > 31 ||
    month < 1 ||
    month > 12
  ) {
    return "";
  }

  const today = todayLocalISO();
  const currentYear = Number(today.slice(0, 4));

  let year = rawYear
    ? Number(rawYear.length === 2 ? `20${rawYear}` : rawYear)
    : currentYear;

  const toIsoIfValid = (y) => {
    const candidate = new Date(y, month - 1, day);

    if (
      candidate.getFullYear() !== y ||
      candidate.getMonth() !== month - 1 ||
      candidate.getDate() !== day
    ) {
      return "";
    }

    return `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  let iso = toIsoIfValid(year);
  if (!iso) return "";

  if (!rawYear && iso < today) {
    year += 1;
    iso = toIsoIfValid(year);
  }

  return iso || "";
}

// Tarkista saako profiilin buukata valitulle päivälle puskurin perusteella.
// Sama logiikka kuin esiintyjän kalenterissa:
// buffer 7 => 7 täyttä puskuripäivää, ensimmäinen sallittu päivä niiden jälkeen.
function isBookableByBuffer(item, selectedDate) {
  if (!item || item?.__kind === "company") return true;

  if (
    !selectedDate ||
    !/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
  ) {
    return true;
  }

  const bufferDays = Math.max(
    0,
    Number(item?.buffer_days ?? 0)
  );

  // Ei puskuria → tänään tai tulevaisuudessa käy.
  if (bufferDays === 0) {
    return selectedDate >= todayLocalISO();
  }

  const today = todayLocalISO();
  const [year, month, day] = today.split("-").map(Number);

  const firstAllowed = new Date(
    year,
    month - 1,
    day
  );

  // Esim. buffer_days = 7:
  // cutoff = tänään + 7 pv
  // ensimmäinen buukattava päivä = cutoff + 1 pv
  firstAllowed.setDate(
    firstAllowed.getDate() + bufferDays + 1
  );

  const y = firstAllowed.getFullYear();
  const m = String(
    firstAllowed.getMonth() + 1
  ).padStart(2, "0");
  const d = String(
    firstAllowed.getDate()
  ).padStart(2, "0");

  const firstAllowedISO = `${y}-${m}-${d}`;

  return selectedDate >= firstAllowedISO;
}

// Palauta talteen otetut etusivun suodattimet (sessionStorage)
function readHomeFilters() {
  try {
    if (typeof sessionStorage === "undefined") return {};
    const raw = sessionStorage.getItem(HOME_FILTERS_KEY);
    if (!raw) return {};
    const j = JSON.parse(raw);
    return (j && typeof j === "object") ? j : {};
  } catch {
    return {};
  }
}

// Palauta AI-haun tila vain profiilista takaisin etusivulle palattaessa.
// Tila kulutetaan heti, jotta vanha AI-haku ei herää myöhemmin uudelleen,
// jos käyttäjä tulee etusivulle jotain muuta reittiä pitkin.
function readHomeAiReturnState() {
  try {
    if (typeof sessionStorage === "undefined") return null;

    const raw = sessionStorage.getItem(HOME_AI_RETURN_KEY);
    if (!raw) return null;

    sessionStorage.removeItem(HOME_AI_RETURN_KEY);

    const parsed = JSON.parse(raw);

    if (
      !parsed ||
      typeof parsed !== "object" ||
      parsed.active !== true ||
      typeof parsed.query !== "string" ||
      !Array.isArray(parsed.results)
    ) {
      return null;
    }

    const selectedDate = String(parsed.selectedDate || "").trim();
    const validSelectedDate =
      !selectedDate || /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
        ? selectedDate
        : "";

    const allowedSources = new Set([
      "url",
      "stored",
      "default",
      "user",
      "ai",
      "ai-range",
      "none",
    ]);

    const selectedDateSource = allowedSources.has(parsed.selectedDateSource)
      ? parsed.selectedDateSource
      : validSelectedDate
        ? "user"
        : "ai-range";

    return {
      active: true,
      query: parsed.query,
      results: parsed.results,
      dateRange:
        parsed.dateRange && typeof parsed.dateRange === "object"
          ? parsed.dateRange
          : null,
      selectedDate: validSelectedDate,
      selectedDateSource,
    };
  } catch {
    try {
      sessionStorage.removeItem(HOME_AI_RETURN_KEY);
    } catch {}

    return null;
  }
}

function getInitialSearchParams() {
  try {
    if (typeof window === "undefined") return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
}

function getInitialQueryValue(key, fallback = "") {
  const p = getInitialSearchParams();
  return p.get(key) || fallback;
}

function getInitialQueryArray(key) {
  const p = getInitialSearchParams();
  return p.getAll(key);
}

function getInitialDateSelection() {
  const today = todayLocalISO();

  try {
    const dFromUrl = getInitialQueryValue("date");

    if (
      dFromUrl &&
      /^\d{4}-\d{2}-\d{2}$/.test(dFromUrl) &&
      dFromUrl >= today
    ) {
      return {
        date: dFromUrl,
        source: "url",
      };
    }

    const saved =
      typeof localStorage !== "undefined"
        ? localStorage.getItem(DATE_KEY)
        : null;

    if (
      saved &&
      /^\d{4}-\d{2}-\d{2}$/.test(saved) &&
      saved >= today
    ) {
      return {
        date: saved,
        source: "stored",
      };
    }
  } catch {}

  return {
    date: today,
    source: "default",
  };
}

// Tapahtumapaikkakunta -> maakunta.
// Paikkakunta EI tarkoita palveluntarjoajan kotipaikkaa, vaan sitä,
// missä asiakkaan tapahtuma järjestetään. Esiintyjät rajataan tämän
// perusteella heidän keikkailualueensa (performance_regions) kautta.
const normalizeMunicipalityKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const MUNICIPALITY_TO_REGION = new Map();
for (const row of Array.isArray(municipalitiesFI) ? municipalitiesFI : []) {
  const regionName = String(
    row?.maakunta ?? row?.region ?? row?.name ?? row?.nimi ?? ""
  ).trim();

  if (!regionName || !Array.isArray(row?.kunnat)) continue;

  for (const municipality of row.kunnat) {
    const key = normalizeMunicipalityKey(municipality);
    if (key) MUNICIPALITY_TO_REGION.set(key, regionName);
  }
}

const getRegionForMunicipality = (value) =>
  MUNICIPALITY_TO_REGION.get(normalizeMunicipalityKey(value)) || "";

function getInitialTypeSelection() {
  const raw = getInitialQueryValue("type", "").toLowerCase();
  const ui = typeParamToUi(raw);
  if (ui === "") return [];
  return ui ? [ui] : [];
}

function readHomeStateFromSearch(search) {
  const p = getSearchParamsFromRaw(search);

  return {
    q: p.get("q") || "",
    location: p.get("location") || "",
    genres: p.getAll("genre"),
    services: p.getAll("service"),
    programs: p.getAll("program"),
    languages: p.getAll("language"),
    facilities: p.getAll("facility"),
    budget: p.get("budget") || "",
    min: p.get("min") || "",
    max: p.get("max") || "",
    sort: p.get("sort") || "recommended",
    date: p.get("date") || "",
    type: p.get("type") || "",
    maakunta: p.get("maakunta") || "",
    wantsBooking: p.get("book") === "1",
    scrollTo: p.get("scrollTo") || "",
    clear: p.get("clear") === "1",
  };
}

function hasExpandedFilterParams(search = "") {
  const state = readHomeStateFromSearch(search);

  return Boolean(
    state.q ||
    state.location ||
    state.genres.length ||
    state.services.length ||
    state.programs.length ||
    state.languages.length ||
    state.facilities.length ||
    state.budget ||
    state.min ||
    state.max
  );
}

function getSearchParamsFromRaw(search = "") {
  try {
    return new URLSearchParams(search || "");
  } catch {
    return new URLSearchParams();
  }
}

function buildHomeSearchParams({
  selectedDate,
  selectedArtistTypes,
  selectedRegions,
  searchTerm,
  locationFilter,
  selectedGenres,
  selectedServices,
  selectedPrograms,
  selectedLanguages,
  selectedPhotoFacilities,
  selectedPriceBucket,
  priceMin,
  priceMax,
  priceSort,
}) {
  const p = new URLSearchParams();

  if (selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
    p.set("date", selectedDate);
  }

  const typeParam = uiTypeToParam(selectedArtistTypes[0] || "");
  if (typeParam) {
    p.set("type", typeParam);
  }

  const maakuntaParam = namesToSlugList(selectedRegions).join(",");
  if (maakuntaParam) {
    p.set("maakunta", maakuntaParam);
  }

  if (searchTerm.trim()) {
    p.set("q", searchTerm.trim());
  }

  if (locationFilter.trim()) {
    p.set("location", locationFilter.trim());
  }

  selectedGenres.forEach((g) => p.append("genre", g));
  selectedServices.forEach((s) => p.append("service", s));
  selectedPrograms.forEach((pr) => p.append("program", pr));
  selectedLanguages.forEach((lng) => p.append("language", lng));
  selectedPhotoFacilities.forEach((f) => p.append("facility", f));

  if (selectedPriceBucket) {
    p.set("budget", selectedPriceBucket);
  }

  if (priceMin !== "") {
    p.set("min", priceMin);
  }

  if (priceMax !== "") {
    p.set("max", priceMax);
  }

  if (priceSort && priceSort !== "recommended") {
    p.set("sort", priceSort);
  }

  return p;
}

function parseItemPrice(item) {
  const v =
    item?.starting_price ??
    item?.startingPrice ??
    item?.starting_price_eur ??
    item?.price_starting ??
    item?.price ??
    null;

  if (v == null || v === "") return null;

  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function compareItemsByPriceAndName(a, b, sortDir = "recommended") {
  // Suositellut = säilytä backendin antama järjestys.
  if (sortDir === "recommended") {
    return 0;
  }

  const pa = parseItemPrice(a);
  const pb = parseItemPrice(b);

  const aOk = Number.isFinite(pa);
  const bOk = Number.isFinite(pb);

  if (aOk && !bOk) return -1;
  if (!aOk && bOk) return 1;

  if (aOk && bOk) {
    const diff = pa - pb;
    const dir = sortDir === "desc" ? -1 : 1;

    if (diff !== 0) {
      return diff * dir;
    }
  }

  const byName = String(a?.name || "").localeCompare(
    String(b?.name || ""),
    "fi",
    { sensitivity: "base" }
  );

  if (byName !== 0) return byName;

  return (Number(a?.id) || 0) - (Number(b?.id) || 0);
}

function getNormalizedTypeVariants(rawType, artistTypeLabelToKey) {
  const lowType = String(rawType || "").toLowerCase().trim();
  if (!lowType) return [];

  const variants = new Set();
  variants.add(lowType);
  variants.add(stripAccents(lowType));

  const keyFromLabel = artistTypeLabelToKey.get(lowType) || lowType;
  variants.add(keyFromLabel);
  variants.add(stripAccents(keyFromLabel));

  const labelFromKey = String(ARTIST_TYPES?.[keyFromLabel]?.label ?? "").toLowerCase().trim();
  if (labelFromKey) {
    variants.add(labelFromKey);
    variants.add(stripAccents(labelFromKey));
  }

  return [...variants].filter(Boolean);
}

// --- listan ja itemien animaatiot (Framer Motion) ---
const listVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.02 },
  },
};
const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.28, ease: "easeOut" } },
  exit:   { opacity: 0, y: -8, transition: { duration: 0.18, ease: "easeIn" } },
};

const formatDateFi = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${Number(d)}.${Number(m)}.${y}`;
};

const FI_MONTH_NAMES = [
  "",
  "tammikuu",
  "helmikuu",
  "maaliskuu",
  "huhtikuu",
  "toukokuu",
  "kesäkuu",
  "heinäkuu",
  "elokuu",
  "syyskuu",
  "lokakuu",
  "marraskuu",
  "joulukuu",
];

const formatAiDateRangeFi = (range) => {
  const from = String(range?.from || "");
  const to = String(range?.to || "");

  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(from) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(to)
  ) {
    return "";
  }

  const [fromYear, fromMonth] = from.split("-").map(Number);
  const [toYear, toMonth] = to.split("-").map(Number);

  if (
    range?.mode === "month" &&
    fromYear === toYear &&
    fromMonth === toMonth
  ) {
    const monthName = FI_MONTH_NAMES[fromMonth] || "";
    return monthName
      ? `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${fromYear}`
      : `${formatDateFi(from)}–${formatDateFi(to)}`;
  }

  return `${formatDateFi(from)}–${formatDateFi(to)}`;
};

const formatCountLabelFi = (n, singular, plural) => {
  return n === 1 ? `1 ${singular}` : `${n} ${plural}`;
};

const formatDefaultResultCountFi = (n, showCompanies = false) => {
  if (showCompanies) {
    return formatCountLabelFi(n, "toimija", "toimijaa");
  }
  return formatCountLabelFi(n, "tekijä", "tekijää");
};

const TYPE_COUNT_LABELS_FI = {
  "bilebändi": ["bilebändi", "bilebändiä"],
  "trubaduuri": ["trubaduuri", "trubaduuria"],
  "duo": ["duo", "duoa"],
  "taikuri": ["taikuri", "taikuria"],
  "valokuvaaja": ["valokuvaaja", "valokuvaajaa"],
  "juontaja": ["juontaja", "juontajaa"],
  "sooloartisti": ["sooloartisti", "sooloartistia"],
  "standup-koomikko": ["standup-koomikko", "standup-koomikkoa"],
  "sirkus & show": ["Sirkus & show -profiili", "Sirkus & show -profiilia"],
  "dj": ["DJ", "DJ:tä"],
};

const formatSelectedTypeCountFi = (n, selectedTypes = [], showCompanies = false) => {
  if (showCompanies) return formatDefaultResultCountFi(n, showCompanies);

  const selected = String(selectedTypes?.[0] || "").trim().toLowerCase();
  const forms = TYPE_COUNT_LABELS_FI[selected];

  if (!forms) return formatDefaultResultCountFi(n, showCompanies);

  return n === 1 ? `1 ${forms[0]}` : `${n} ${forms[1]}`;
};

const formatResultsScopeFi = (locationValue = "", regions = []) => {
  if (locationValue) return `tapahtumapaikkana ${locationValue}`;
  if (regions.length === 1) return `alueella ${regions[0]}`;
  if (regions.length > 1) return `alueilla ${regions.join(", ")}`;
  return "koko Suomessa";
};

// --- safe text helpers ---
const textOf = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(textOf).join(" ");
  if (typeof v === "object") {
    const known = ["label", "name", "region", "url"].map(k => v[k]).filter(Boolean);
    return (known.length ? known : Object.values(v)).map(textOf).join(" ");
  }
  return String(v);
};
const lower = (v) => textOf(v).toLowerCase();

// esim. "Bändi" -> "bandi" (helpottaa matchausta jos backendissä ei ole ääkkösiä)
const stripAccents = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/* ---------- Tilaisuudet / ohjelmat: apurit ---------- */
const EVENT_TYPE_FALLBACK_LABELS = {
  ravintola: "Ravintola / baari",
  festarit: "Festivaalit",
  yksityiskeikka: "Yksityiskeikka",
  haat: "Häät",
  yritystilaisuus: "Yritystilaisuus",
  syntymapaiva: "Syntymäpäivä",
  "syntymäpäivät": "Syntymäpäivä",
};

const prettyFromSlug = (slug) => {
  const s = String(slug || "").trim();
  if (!s) return "";
  const key = s.toLowerCase();
  if (EVENT_TYPE_FALLBACK_LABELS[key]) return EVENT_TYPE_FALLBACK_LABELS[key];
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
};

// array | object[] | string(, / JSON / rivinvaihdot) -> string[]
const toStringArray = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) {
    return val
      .map((x) =>
        typeof x === "object" && x
          ? (x.label ?? x.name ?? x.region ?? x.title ?? "")
          : String(x)
      )
      .map((s) => String(s).trim())
      .filter(Boolean);
  }
  const s0 = String(val).trim();
  if (!s0) return [];
  // JSON-taulukko
  if (s0.startsWith("[") && s0.endsWith("]")) {
    try {
      const j = JSON.parse(s0);
      if (Array.isArray(j)) return toStringArray(j);
    } catch {/* ignore */}
  }
  // rivinvaihdot/pilkut
  return s0
    .replace(/\n+/g, ",")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const extractPrograms = (band = {}) => {
  // Palauta ohjelmien / esitysmuotojen ID:t hakusuodatusta varten.
  const slugs = [
    ...toStringArray(band?.additional_services),
    ...toStringArray(band?.nonmusic_style_services),
  ]
    .map((s) => String(s).trim())
    .filter(Boolean);

  if (slugs.length) return [...new Set(slugs)];

  const fromStr = String(band?.additional_services_string || "").trim();
  if (fromStr) {
    const arr = toStringArray(fromStr).map((s) => String(s).trim()).filter(Boolean);
    if (arr.length) return arr;
  }

  // Jos backend antaa vain labelit, yritä mapata ne catalogin id:ihin (esim. "Lavashow" -> "magic-stage")
  const labels = toStringArray(band?.additional_service_labels)
    .map((s) => String(s).trim())
    .filter(Boolean);
  if (!labels.length) return [];

  const slugify = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  return labels
    .map((lbl) => {
      const key = String(lbl).trim().toLowerCase();
      // jos label näyttää jo slugilta (sisältää - tai _), pidä se
      if (/[._-]/.test(lbl)) return String(lbl).trim().toLowerCase().replace(/_/g, "-");
      const hit = PROGRAM_CATALOG.find((p) => String(p.label).trim().toLowerCase() === key);
      return hit?.id || slugify(lbl);
    })
    .filter(Boolean);
};

// Kielet: yritä löytää “ihmislabelit” ensin, fallbackina koodit/stringit
const extractLanguages = (entity = {}) => {
  if (entity?.__kind === "company") return [];

  // 1) suorat labelit (paras)
  const labels = toStringArray(
    entity?.language_labels ??
    entity?.languages_labels ??
    entity?.languageLabels
  );
  if (labels.length) return labels;

  // 2) fallback: eri mahdollisia avaimia
  const arr = [
    ...toStringArray(entity?.languages),
    ...toStringArray(entity?.language_codes),
    ...toStringArray(entity?.languages_string ?? entity?.language_string),
    ...toStringArray(entity?.spoken_languages ?? entity?.spoken_languages_string),
  ];

  return arr.map((s) => String(s).trim()).filter(Boolean);
};

// Valokuvaajan puitteet / studio
const extractPhotoFacilities = (entity = {}) => {
  if (entity?.__kind === "company") return [];

  const labels = toStringArray(
    entity?.photo_facility_labels ??
    entity?.photo_facilities_labels ??
    entity?.photoFacilitiesLabels
  );
  if (labels.length) return labels;

  return toStringArray(
    entity?.photo_facilities ??
    entity?.photoFacilities ??
    entity?.photo_facilities_string
  );
};

const hasOwnStudioFacility = (entity = {}) => {
  const ids = toStringArray(entity?.photo_facility_ids).map((x) => String(x).trim());
  if (ids.includes("1")) return true;

  const facilities = extractPhotoFacilities(entity)
    .map((x) => String(x).toLowerCase().trim());

  // ⚠️ Älä matchaa kaikkia "studio"-sanoja, muuten myös
  // "Vuokrastudio käytettävissä" osuu väärin.
  return facilities.some((f) =>
    f === "oma studio" || f === "oma-studio"
  );
};

// --- normalisoi genret palvelimen vastauksesta {items:[{id,name}]} tai suoraan taulukosta
function normalizeGenreNames(raw) {
  const list = Array.isArray(raw?.items) ? raw.items : (Array.isArray(raw) ? raw : []);
  const names = list
    .map(g => typeof g === "string" ? g : (g?.name ?? g?.label ?? g?.title ?? ""))
    .map(s => String(s).trim())
    .filter(Boolean);

  // dedupe case-insensitiivisesti, säilytä 1. kirjoitusasu
  const seen = new Set();
  const uniq = [];
  for (const n of names) {
    const key = n.toLowerCase();
    if (!seen.has(key)) { seen.add(key); uniq.push(n); }
  }

  // aakkosjärjestys (fi)
  uniq.sort((a, b) => a.localeCompare(b, "fi", { sensitivity: "base" }));
  return uniq;
}

// --- apuri: vertaa taulukoita järjestyksestä riippumatta ---
const eqArrIgnoreOrder = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const A = [...a].map((s) => String(s).toLowerCase()).sort();
  const B = [...b].map((s) => String(s).toLowerCase()).sort();
  return A.every((v, i) => v === B[i]);
};

export default function BandList() {
  const navigate = useNavigate();
  const rrLocation = useLocation();
  const [showAllLandingLinks, setShowAllLandingLinks] = useState(false);

    const [isMobileHero, setIsMobileHero] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  // ✅ Teema (light/dark) — luetaan <html class="dark"> perusteella
  const [theme, setTheme] = useState(() => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
  });

  const [showEmbedToast, setShowEmbedToast] = useState(false);
  const embedToastShowTimerRef = useRef(null);
  const embedToastHideTimerRef = useRef(null);
  const embedToastDurationSec = HOME_EMBED_TOAST_HIDE_MS / 1000;

  // ✅ Päivitä teema kun dark-luokka vaihtuu (SPA + toggle)
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() => {
      setTheme(document.documentElement.classList.contains("dark") ? "dark" : "light");
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;

    const media = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobileHero(e.matches);

    setIsMobileHero(media.matches);

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  // ✅ Valitse video/poster teeman mukaan
const heroVideoSrc = theme === "dark" ? heroVideoDark : heroVideoLight;
const heroPoster = theme === "dark" ? heroPosterDark : heroPosterLight;
const heroMp4Src = isMobileHero ? undefined : heroVideoSrc;

// ✅ Lue suodattimet kerran (pysyy kun käyt profiilissa ja palaat takaisin)
  const homeFiltersRef = useRef(null);
  if (homeFiltersRef.current === null) {
    homeFiltersRef.current = readHomeFilters();
  }
  const homeFilters = homeFiltersRef.current || {};

  // AI-haku tarvitsee profiilista takaisin palatessa muutakin kuin URL-filtterit:
  // hakulauseen, backendin tulosjoukon ja month/range-intentin.
  // Luetaan snapshot vain kerran tämän etusivu-mountin aikana.
  const homeAiReturnStateRef = useRef(undefined);

  if (homeAiReturnStateRef.current === undefined) {
    homeAiReturnStateRef.current = readHomeAiReturnState();
  }

  const homeAiReturnState = homeAiReturnStateRef.current;

  // Päivämäärä alustetaan samasta snapshotista kuin AI-tila.
  // Tämä on tärkeää month/range-haulle: tyhjä selectedDate on siinä oikea arvo,
  // eikä sitä saa muuttaa mountissa tämän päivän päivämääräksi.
  const initialDateSelectionRef = useRef(undefined);

  if (initialDateSelectionRef.current === undefined) {
    initialDateSelectionRef.current = homeAiReturnState?.active
      ? {
          date: homeAiReturnState.selectedDate,
          source: homeAiReturnState.selectedDateSource,
        }
      : getInitialDateSelection();
  }

  // Ilmoita headerille sivun otsikko (mittausta varten)
  useEffect(() => {
    const path = `${rrLocation.pathname}${rrLocation.search || ""}`;
    window.dispatchEvent(
      new CustomEvent("bbx:title", {
        detail: {
          title: "Varaa esiintyjä tai tapahtumapalvelu | Buukkeri",
          path,
        },
      })
    );
 }, [rrLocation.pathname, rrLocation.search]);

  // ---------- Tilat ----------
  const [bands, setBands] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => getInitialQueryValue("q"));
  const [locationFilter, setLocationFilter] = useState(() => getInitialQueryValue("location"));
  const [showMoreFilters, setShowMoreFilters] = useState(() => {
   return hasExpandedFilterParams(rrLocation.search) || !!homeFilters.showMoreFilters;
 });

  // Ei oletusta: luetaan vain jos URLissa on ?type=...
  const [selectedArtistTypes, setSelectedArtistTypes] = useState(() => {
    return getInitialTypeSelection();                          // ei paramia → ei esivalintaa
  });

  const [selectedLanguages, setSelectedLanguages] = useState(() => {
    try {
      return getInitialQueryArray("language");
    } catch { return []; }
  });
  const [selectedPhotoFacilities, setSelectedPhotoFacilities] = useState(() => {
    try {
      return getInitialQueryArray("facility");
    } catch { return []; }
  });

    // ---------- Suodatukset ----------
  const [selectedRegions, setSelectedRegions] = useState([]);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const debouncedLocation = useDebouncedValue(locationFilter, 250);
  const debouncedRegions = useDebouncedValue(selectedRegions, 120); // labeliin ok
  const locationRegion = useMemo(
    () => getRegionForMunicipality(debouncedLocation),
    [debouncedLocation]
  );
  // Tapahtumapaikkakunta on tarkempi rajaus ja voittaa maakuntavalinnan.
  // Esim. Kerava -> Uusimaa. Backend hakee tämän perusteella alueella
  // keikkailevat esiintyjät performance_regions-tiedon kautta.
  const regionForApi = locationRegion || selectedRegions?.[0] || "";

  const [availableGenres, setAvailableGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState(() => {
    try {
      return getInitialQueryArray("genre");
    } catch {
      return [];
    }
  });

  // Ohjelmat (uudet esiintyjätyypit)
  const [selectedPrograms, setSelectedPrograms] = useState(() => {
    try {
      return getInitialQueryArray("program");
    } catch {
      return [];
    }
  });

   // 💶 Budjetti (front-suodatin)
  const [selectedPriceBucket, setSelectedPriceBucket] = useState(() => {
    return getInitialQueryValue("budget");
  });
  const [priceMin, setPriceMin] = useState(() => {
    return getInitialQueryValue("min");
  });
  const [priceMax, setPriceMax] = useState(() => {
    return getInitialQueryValue("max");
  });
  const [priceSort, setPriceSort] = useState(() => {
    return getInitialQueryValue("sort", "recommended");
  });

  // ---------- AI-haku ----------
  const [aiQuery, setAiQuery] = useState(
    () => homeAiReturnState?.query || ""
  );
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchError, setAiSearchError] = useState("");
  const [aiSearchResults, setAiSearchResults] = useState(
    () => homeAiReturnState?.results || []
  );
  const [aiSearchActive, setAiSearchActive] = useState(
    () => Boolean(homeAiReturnState?.active)
  );
  const [aiDateRange, setAiDateRange] = useState(
    () => homeAiReturnState?.dateRange || null
  );

  // Kun aktiivista AI-hakua tarkennetaan käsin tarkalle päivälle,
  // tarkistetaan kyseisen päivän todellinen saatavuus erikseen.
  // keys = esim. ["id:53", "slug:bilebandi-joyride"].
  const [aiExactDateAvailability, setAiExactDateAvailability] = useState({
    date: "",
    keys: null,
  });
  const aiExactDateAvailabilityRequestRef = useRef(0);

  const aiDateRangeLabel = useMemo(
    () => formatAiDateRangeFi(aiDateRange),
    [aiDateRange]
  );

  // Palvelut (yrityksille) – pidetään tila olemassa, mutta ei käytetä kun SHOW_COMPANIES=false
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState(() => {
    try { return getInitialQueryArray("service"); } catch { return []; }
  });
  const [availableRegions, setAvailableRegions] = useState([]);

  // Estetään ensimmäisen renderin "räpsyntä" kunnes URL → state on tehty
  const [initialSyncDone, setInitialSyncDone] = useState(false);

  const getResponsivePageSize = () => {
    if (typeof window === "undefined") return 8;

    // 2xl-gridissä näytetään 5 korttia/rivi → 2 riviä = 10 korttia
    if (window.matchMedia("(min-width: 1536px)").matches) return 10;

    // xl-gridissä näytetään 4 korttia/rivi → 2 riviä = 8 korttia
    if (window.matchMedia("(min-width: 1280px)").matches) return 8;

    // md/lg-gridissä näytetään 3 korttia/rivi → 2 riviä = 6 korttia
    if (window.matchMedia("(min-width: 768px)").matches) return 6;

    // mobiilissa pidetään nykyinen 8
    return 8;
  };

  const [pageSize, setPageSize] = useState(() => getResponsivePageSize());
  const [visibleCount, setVisibleCount] = useState(() => getResponsivePageSize());

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updatePageSize = () => {
      const next = getResponsivePageSize();
      setPageSize(next);
      setVisibleCount((current) => {
        // Jos käyttäjä ei ole painanut "näytä lisää", pidetään aina 2 täyttä riviä.
        // Jos hän on painanut, ei pienennetä näkyvää määrää turhaan.
        return current <= pageSize ? next : Math.max(current, next);
      });
    };

    updatePageSize();

    window.addEventListener("resize", updatePageSize);
    return () => window.removeEventListener("resize", updatePageSize);
  }, [pageSize]);

  // Päivämäärän arvo ja lähde pidetään erillään.
  // Näin localStoragesta palautettu vanha päivä ei vuoda uuteen AI-hakuun.
  const [selectedDate, setSelectedDate] = useState(
    () => initialDateSelectionRef.current.date
  );
  const [selectedDateSource, setSelectedDateSource] = useState(
    () => initialDateSelectionRef.current.source
  );

  // Kun päivämäärä muuttuu state-lähteestä (käyttäjä tai AI), state -> URL
  // ja URL -> state -effectit voivat osua samaan render-kierrokseen.
  // Muistetaan uudempi state-arvo, jotta vielä vanhaa URL-arvoa lukeva effect
  // ei ehdi kumota käyttäjän valintaa tai AI:n exact/month/range-tulkintaa.
  // null = ei odottavaa synkkaa; tyhjä string = päivämäärä poistettiin URL:sta.
  const pendingDateUrlSyncRef = useRef(null);

  const [showBooking, setShowBooking] = useState(false);
  const [scrolledOnce, setScrolledOnce] = useState(false);
  const [showDeferredHomeSections, setShowDeferredHomeSections] = useState(false);
  const [nextAvailableDays, setNextAvailableDays] = useState([]);
  const [nextAvailableLoading, setNextAvailableLoading] = useState(false);
  const [nextAvailableSearched, setNextAvailableSearched] = useState(false);

  const addDaysToIso = useCallback((isoDate, daysToAdd) => {
    if (!isoDate) return todayLocalISO();
    const [year, month, day] = String(isoDate).split("-").map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + daysToAdd);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);

  const resetDetailFilters = useCallback(() => {
    setSelectedGenres([]);
    setSelectedServices([]);
    setSelectedPrograms([]);
    setSelectedLanguages([]);
    setSelectedPhotoFacilities([]);
  }, []);

  const clearPriceFilters = useCallback(() => {
    setSelectedPriceBucket("");
    setPriceMin("");
    setPriceMax("");
    setPriceSort("recommended");
  }, []);

  // 💶 Budjetti-min/max: salli tyhjä, mutta estä negatiiviset ja muut roska-arvot
  // - type="number" hyväksyy monessa selaimessa myös "-" ja "e"
  // - clampataan joka muutoksessa niin state ei koskaan sisällä negatiivista
  const sanitizeNonNegativeInt = (raw) => {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    const n = Math.floor(Number(s.replace(",", ".")));
    if (!Number.isFinite(n)) return "";
    return String(Math.max(0, n));
  };

  // ✅ Persistoi suodattimet sessionStorageen aina kun ne muuttuvat
  useEffect(() => {
    try {
      sessionStorage.setItem(
        HOME_FILTERS_KEY,
        JSON.stringify({
          searchTerm,
          locationFilter,
          showMoreFilters,
          selectedLanguages,
          selectedGenres,
          selectedServices,
          selectedPrograms,
          selectedPhotoFacilities,
           selectedPriceBucket,
           priceMin,
           priceMax,
           priceSort
        })
      );
    } catch {}
  }, [
    searchTerm,
    locationFilter,
    selectedLanguages,
    showMoreFilters,
    selectedGenres,
    selectedServices,
    selectedPrograms,
    selectedPhotoFacilities,
     selectedPriceBucket,
     priceMin,
     priceMax,
     priceSort,
  ]);

  // Scrollaa pehmeästi tuloksiin (RESULTS_ID) – huomioi kiinteä header
  const scrollToResults = useCallback(() => {
    const el = document.getElementById(RESULTS_ID);
    if (!el) return;

    const headerOffset = 96; // px – säädä jos headerin korkeus muuttuu
    const rect = el.getBoundingClientRect();
    const offset = rect.top + window.scrollY - headerOffset;

    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }, []);

  // Tekstikentät (nimi/kotipaikka/min/max):
  // - ÄLÄ scrollaa jokaisella näppäimellä (se aiheutti Macbookilla "hypyn alas")
  const onCommitScrollKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        scrollToResults();
      }
    },
    [scrollToResults]
  );

  // 🟣 BOOT-OVERLAY: neljän haun valmius
  const [genresReady, setGenresReady]   = useState(false);
  const [typesReady, setTypesReady]     = useState(false);
  const [listReady, setListReady]       = useState(false);
  const [regionsReady, setRegionsReady] = useState(false);
  const [bootLoading, setBootLoading]   = useState(() => {
    try {
      // 0) devissä/localhostissa ei overlayta
      if (IS_DEV) return false;
      // 0.5) mobiilissa ei overlayta
      if (isMobileViewport()) return false;
      // 1) jos jo näytetty tässä välilehdessä → ei overlayta
      if (sessionStorage.getItem(HOME_BOOT_KEY) === "1") return false;
      // 2) reload/back_forward → ei overlayta
      let navType = "navigate";
      try {
        const nav = performance.getEntriesByType?.("navigation")?.[0];
        navType =
          nav?.type ||
          (performance.navigation &&
            ["navigate", "reload", "back_forward", "prerender"][performance.navigation.type]);
      } catch {}
      if (navType === "reload" || navType === "back_forward") return false;
      // 3) sisäinen referrer → ei overlayta
      if (hasInternalReferrer && hasInternalReferrer()) return false;
      // 4) muuten näytä overlay kerran
      sessionStorage.setItem(HOME_BOOT_KEY, "1");
      return true;
    } catch { return false; }
  });

  useEffect(() => {
  if (isMobileHero && bootLoading) {
    setBootLoading(false);
  }
}, [isMobileHero, bootLoading]);

  // Ajastusrefit
  const bootStartRef = useRef(null);
  const bootHideTimerRef = useRef(null);
  const bootFailTimerRef = useRef(null);
  // Käynnistä hätäkatkaisu vain jos overlay oikeasti näytetään
  useEffect(() => {
    if (!bootLoading) return;
    bootStartRef.current = performance.now();
    try { sessionStorage.setItem(HOME_BOOT_KEY, "1"); } catch {}
    bootFailTimerRef.current = setTimeout(() => setBootLoading(false), BOOT_FAILSAFE_MS);
    return () => {
      clearTimeout(bootFailTimerRef.current);
      clearTimeout(bootHideTimerRef.current);
    };
  }, [bootLoading]);

// Lykätään etusivun alapuolen osiot ensilatauksen jälkeen.
// Näin LatestArtists ja MonthlyFeatured eivät käynnistä /bands?limit=50&published=1
// -hakua samaan aikaan päähaun, genrejen, alueiden ja event-types-haun kanssa.
useEffect(() => {
  if (bootLoading) return;
  if (showDeferredHomeSections) return;
  if (typeof window === "undefined") return;

  let cancelled = false;

  const timeoutId = window.setTimeout(() => {
    if (!cancelled) {
      setShowDeferredHomeSections(true);
    }
  }, BELOW_FOLD_DEFER_MS);

  return () => {
    cancelled = true;
    window.clearTimeout(timeoutId);
  };
}, [bootLoading, showDeferredHomeSections]);

  useEffect(() => {
    if (bootLoading) return;

    try {
      if (sessionStorage.getItem(HOME_EMBED_TOAST_KEY) === "1") return;
    } catch {}

    clearTimeout(embedToastShowTimerRef.current);
    clearTimeout(embedToastHideTimerRef.current);

    embedToastShowTimerRef.current = setTimeout(() => {
      setShowEmbedToast(true);

      embedToastHideTimerRef.current = setTimeout(() => {
        setShowEmbedToast(false);
        try {
          sessionStorage.setItem(HOME_EMBED_TOAST_KEY, "1");
        } catch {}
      }, HOME_EMBED_TOAST_HIDE_MS);
    }, HOME_EMBED_TOAST_DELAY_MS);

    return () => {
      clearTimeout(embedToastShowTimerRef.current);
      clearTimeout(embedToastHideTimerRef.current);
    };
  }, [bootLoading]);

  const closeEmbedToast = useCallback(() => {
    setShowEmbedToast(false);
    clearTimeout(embedToastShowTimerRef.current);
    clearTimeout(embedToastHideTimerRef.current);
    try {
      sessionStorage.setItem(HOME_EMBED_TOAST_KEY, "1");
    } catch {}
  }, []);

// Poista bbx-booting heti kun overlay ei ole käytössä
// (tai overlay on ehtinyt jo sulkeutua). Overlay itse huolehtii
// bbx-overlay-open -luokasta.
useEffect(() => {
  try {
    if (!bootLoading) {
      document.documentElement.classList.remove("bbx-booting");
    }
  } catch {}
}, [bootLoading]);

// (poistettu duplikaatti-efekti)

  // Piilota overlay vasta, kun kaikki kyselyt valmiit JA miniminäyttöaika on kulunut
  useEffect(() => {
    if (!bootLoading) return;
    if (!(genresReady && typesReady && listReady && regionsReady)) return;
    const started = bootStartRef.current ?? performance.now();
    const elapsed = performance.now() - started;
    const wait = Math.max(0, BOOT_MIN_SHOW_MS - elapsed);
    clearTimeout(bootHideTimerRef.current);
    bootHideTimerRef.current = setTimeout(() => setBootLoading(false), wait);
  }, [genresReady, typesReady, listReady, regionsReady, bootLoading]);

  // ---------- Helpers ----------
  const closeBooking = useCallback(() => {
    setShowBooking(false);
  }, []);

const normalizeArtist = (b) => {
  const o = {
    __kind: "artist",
    // säilytä KAIKKI alkuperäiset kentät (mm. instruments*, additional_services* jne.)
    ...b,
    // normalisoi vain nämä kaksi numeerista
    average_rating: Number(b?.average_rating ?? b?.rating?.average ?? 0),
    reviews_count: Number(b?.reviews_count ?? b?.total_reviews ?? b?.rating?.total ?? 0),
  };

  // Älä riko alkuperäistä genres/regions -dataa: lisää vain fallbackit jos puuttuu
  if (!o.genres || (Array.isArray(o.genres) && o.genres.length === 0)) {
    if (typeof b?.genre_string === "string") {
      o.genres = b.genre_string.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  if (!o.regions) {
    o.regions = b?.performance_regions ?? [];
  }

  return o;
};

  const normalizeCompany = (c) => ({
    __kind: "company",
    id: c.id,
    slug: c.slug,
    name: c.name,
    profile_image: c.profile_image,
    location: c.location,
    regions: c.regions ?? c.service_regions ?? [],
    genres: [],
    artist_type: "Yritys",
    first_video_url: c.first_video_url,
    video_link: c.video_link,
    videos: c.videos,
    average_rating: Number(c.average_rating ?? 0),
    reviews_count: Number(c.reviews_count ?? 0),
    services: Array.isArray(c.services) ? c.services : [],
  });

  const extractGenres = (band = {}) => {
    if (Array.isArray(band.genres)) return band.genres;
    if (typeof band.genre_string === "string")
      return band.genre_string.split(",").map((s) => s.trim());
    if (typeof band.genre === "string")
      return band.genre.split(",").map((s) => s.trim());
    return [];
  };

  // Debounce hook
  function useDebouncedValue(value, delay = 250) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const t = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
  }

  // --- Johdetut tilat: tyyppi → company/artist, ja onko tagit pois käytöstä
  const selectedType = (selectedArtistTypes[0] || "").toLowerCase();
  const isCompanyMode = SHOW_COMPANIES && selectedType === "yritys"; // yritystilaa ei voi aktivoida kun flag=false
  const tagsDisabled = !selectedType;

  // ARTIST_TYPES voi olla muodossa { band: {label:"Bändi (oma musiikki)"}, ... }
  // Tehdään label -> key -kartta, jotta match toimii riippumatta siitä palauttaako backend keyn vai labelin.
  const artistTypeLabelToKey = useMemo(() => {
    const m = new Map();
    for (const [key, obj] of Object.entries(ARTIST_TYPES || {})) {
      const label = String(obj?.label ?? "").trim();
      if (label) m.set(label.toLowerCase(), key);
    }
    return m;
  }, []);

  // ✅ Tyyppikohtaiset ohjelmavaihtoehdot (ei riipu tuloslistasta)
  const programsUI = useMemo(() => {
    if (!selectedArtistTypes?.[0] || isCompanyMode) return [];
    return programOptionsForType(selectedArtistTypes[0]);
  }, [selectedArtistTypes, isCompanyMode]);

  const availableLanguages = useMemo(() => {
    const set = new Set();
    (bands || []).forEach((b) => {
      extractLanguages(b).forEach((l) => set.add(l));
    });
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, "fi", { sensitivity: "base" })
    );
  }, [bands]);

  const isMusicTypeSelected = !!selectedType && MUSIC_TYPE_KEYS.some((k) => selectedType.includes(k));
  const isPhotographerTypeSelected =
    !!selectedType &&
    ["valokuvaaja", "photographer"].some((k) => selectedType.includes(k));
  
  const isCircusTypeSelected =
  !!selectedType &&
  ["sirkus", "circus", "akrobat"].some((k) => selectedType.includes(k));

  // Lisäsuodattimien moodi:
  // - musatyyppi => näytä Musiikkityyli (genret)
  // - valokuvaaja => tällä hetkellä vain Tilaisuus (+ myöhemmin omat photo-filtterit)
  // - muu ei-musa => näytä Tilaisuudet + Ohjelmat + Kielet
  const showGenreFilter = !isCompanyMode && !tagsDisabled && isMusicTypeSelected;
  const showEventProgramFilters = !isCompanyMode && !tagsDisabled && !isMusicTypeSelected && !isPhotographerTypeSelected;
  const showPhotographerFilters = !isCompanyMode && !tagsDisabled && isPhotographerTypeSelected;

  // Jos flag on false ja jokin tapa yrittää lukita "Yritys" tyypin (esim. vanha URL-state),
  // poistetaan se siististi.
  useEffect(() => {
    if (!SHOW_COMPANIES) {
      const next = selectedArtistTypes.filter(
        (t) => String(t).toLowerCase() !== "yritys"
      );
      if (next.length !== selectedArtistTypes.length) {
        setSelectedArtistTypes(next);
      }
    }
  }, [selectedArtistTypes]);

  // ---------- Efektit: AOS (käynnistä vasta overlayn jälkeen) ----------
  useEffect(() => {
    if (bootLoading) return;
    AOS.init({
      duration: 700,
      easing: "ease-out",
      offset: 80,
      once: true,                 // ei turhaa "läsähtelyä"
      anchorPlacement: "top-bottom",
    });
  }, [bootLoading]);

  useEffect(() => {
    if (bootLoading) return;
    if (typeof AOS?.refreshHard === "function") AOS.refreshHard();
  }, [visibleCount, bootLoading]);

  // ---------- Efekti: header-event ----------
  useEffect(() => {
    const onOpen = () => {
      setShowBooking(true);
      trackBookingModalOpened();
    };

    window.addEventListener("bilebox:openBooking", onOpen);
    return () => window.removeEventListener("bilebox:openBooking", onOpen);
  }, []);

useEffect(() => {
  if (!initialSyncDone) return;

  const urlState = readHomeStateFromSearch(rrLocation.search);

  setSearchTerm((prev) => (prev === urlState.q ? prev : urlState.q));
  setLocationFilter((prev) => (prev === urlState.location ? prev : urlState.location));
  setSelectedGenres((prev) => (eqArrIgnoreOrder(prev, urlState.genres) ? prev : urlState.genres));
  setSelectedServices((prev) => (eqArrIgnoreOrder(prev, urlState.services) ? prev : urlState.services));
  setSelectedPrograms((prev) => (eqArrIgnoreOrder(prev, urlState.programs) ? prev : urlState.programs));
  setSelectedLanguages((prev) => (eqArrIgnoreOrder(prev, urlState.languages) ? prev : urlState.languages));
  setSelectedPhotoFacilities((prev) => (eqArrIgnoreOrder(prev, urlState.facilities) ? prev : urlState.facilities));
  setSelectedPriceBucket((prev) => (prev === urlState.budget ? prev : urlState.budget));
  setPriceMin((prev) => (prev === urlState.min ? prev : urlState.min));
  setPriceMax((prev) => (prev === urlState.max ? prev : urlState.max));
  setPriceSort((prev) => (prev === urlState.sort ? prev : urlState.sort));
}, [rrLocation.search, initialSyncDone]);

// --- URL → state: clear=1 nollaa kaikki filtterit (paitsi TYYPPI ja PVM) ---
useEffect(() => {
  const urlState = readHomeStateFromSearch(rrLocation.search);
  if (!urlState.clear) return;

  const p = getSearchParamsFromRaw(rrLocation.search);

  // 1) Nollaa UI-filtterit
  setSearchTerm("");
  setLocationFilter("");
  resetDetailFilters();
  clearPriceFilters();
  try { sessionStorage.removeItem(HOME_FILTERS_KEY); } catch {}
  // Huom: ÄLÄ nollaa maakuntia täällä. clear=1 linkeillä halutaan usein säilyttää ?maakunta=...
  // (Jos tarvitset joskus "täysnollauksen", tee sille oma parami, esim. clearAll=1)

  // 2) Siivoa URL (säilytä maakunta; poistetaan vain clear-flag)
   p.delete("clear");
  navigate(
    { pathname: rrLocation.pathname, search: `?${p.toString()}`, hash: rrLocation.hash },
    { replace: true }
  );
}, [rrLocation.search, rrLocation.pathname, rrLocation.hash, navigate, resetDetailFilters, clearPriceFilters]);

// --- URL → state: ?maakunta=uusimaa,pirkanmaa (jo olemassa yllä)
// --- Muista maakunnat localStorageen, jos URL:ssa EI ole ?maakunta ---
useEffect(() => {
  const p = new URLSearchParams(rrLocation.search);
  const hasUrl = !!(p.get("maakunta") || "");
  if (hasUrl) return; // URL voittaa aina
  try {
    const raw = localStorage.getItem(REGIONS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const arr = Array.isArray(parsed) ? parsed : [];
    const sanitized = availableRegions.length
      ? arr.filter((n) => availableRegions.includes(n))
      : arr;
    setSelectedRegions((prev) =>
      eqArrIgnoreOrder(prev, sanitized) ? prev : sanitized
    );
  } catch {/* ignore */}
}, [availableRegions, rrLocation.search]);

// --- state → localStorage: tallenna maakuntavalinnat ---
useEffect(() => {
  try {
    localStorage.setItem(REGIONS_KEY, JSON.stringify(selectedRegions));
  } catch {/* ignore */}
}, [selectedRegions]);

// Merkitse initial-sync valmiiksi, kun URL → state on asettunut vähintään kerran
useEffect(() => {
  if (!initialSyncDone) {
    setInitialSyncDone(true);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedDate, selectedArtistTypes.length, selectedRegions.length]);

// --- URL → state: ?maakunta=uusimaa,pirkanmaa ---
useEffect(() => {
  if (!initialSyncDone) return; // älä synkkaa ennen kuin initial URL→state tehty
  const p = new URLSearchParams(rrLocation.search);
  const raw = p.get("maakunta") || "";
  if (!raw) return;
  // Slugit -> viralliset nimet
  const fromUrl = slugParamToNames(raw);
  // Jos palvelin palauttaa rajatun listan, pidä vain sallitut
  const target = availableRegions.length
    ? fromUrl.filter((n) => availableRegions.includes(n))
    : fromUrl;
  setSelectedRegions((prev) => (eqArrIgnoreOrder(prev, target) ? prev : target));
// availableRegions riippuvuudessa, jotta URL:sta luettu säilyy kun nimet on ladattu
}, [rrLocation.search, availableRegions, initialSyncDone]);

useEffect(() => {
  if (!initialSyncDone) return;

  const p = buildHomeSearchParams({
    selectedDate,
    selectedArtistTypes,
    selectedRegions,
    searchTerm,
    locationFilter,
    selectedGenres,
    selectedServices,
    selectedPrograms,
    selectedLanguages,
    selectedPhotoFacilities,
    selectedPriceBucket,
    priceMin,
    priceMax,
    priceSort,
  });

  const nextSearch = p.toString() ? `?${p.toString()}` : "";
  const currentSearch = rrLocation.search || "";

  if (nextSearch === currentSearch) return;

  navigate(
    {
      pathname: rrLocation.pathname,
      search: nextSearch,
      hash: rrLocation.hash,
    },
    { replace: true }
  );
}, [
  initialSyncDone,
  selectedDate,
  selectedArtistTypes,
  selectedRegions,
  searchTerm,
  locationFilter,
  selectedGenres,
  selectedServices,
  selectedPrograms,
  selectedLanguages,
  selectedPhotoFacilities,
  selectedPriceBucket,
  priceMin,
  priceMax,
  priceSort,
  rrLocation.pathname,
  rrLocation.search,
  rrLocation.hash,
  navigate,
]);

  useEffect(() => {
    if (!initialSyncDone) return; // estä ensimmäinen kierros

    const urlState = readHomeStateFromSearch(rrLocation.search);
    const d = urlState.date;
    const today = todayLocalISO();

    // Päivämäärä voi juuri olla muuttunut joko käyttäjän toimesta tai AI-haun
    // exact/month/range-tulkinnasta. State -> URL -synkka voi olla vielä kesken,
    // jolloin rrLocation.search sisältää hetken vanhan päivän.
    // Älä kirjoita stale-URL-päivää takaisin uudemman staten päälle.
    const pendingDate = pendingDateUrlSyncRef.current;

    if (pendingDate !== null) {
      // URL on nyt saavuttanut staten viimeisimmän arvon.
      // selectedDateSource säilytetään sellaisena kuin muutoksen tekijä sen asetti.
      if (d === pendingDate) {
        pendingDateUrlSyncRef.current = null;
        return;
      }

      // URL on vielä jäljessä. Nykyinen state on uudempi arvo,
      // joten odota state -> URL -effectin seuraavaa kierrosta.
      if (selectedDate === pendingDate) {
        return;
      }

      // State muuttui jostain muusta syystä ennen URL-synkan valmistumista.
      pendingDateUrlSyncRef.current = null;
    }

    if (
      d &&
      /^\d{4}-\d{2}-\d{2}$/.test(d) &&
      d >= today
    ) {
      setSelectedDate((previousDate) => {
        if (previousDate === d) {
          return previousDate;
        }

        setSelectedDateSource("url");
        return d;
      });
    } else if (d && d < today) {
      setSelectedDate(today);
      setSelectedDateSource("default");
    }
    // ei siivoa URL:ia; vain synkkaa lomakkeen
  }, [rrLocation.search, initialSyncDone, selectedDate]);

  // --- TALLENNA valittu päivä localStorageen aina kun muuttuu ---
  useEffect(() => {
    try {
      if (selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
        localStorage.setItem(DATE_KEY, selectedDate);
      }
    } catch {}
  }, [selectedDate]);

  // Jos AI-haku on aktiivinen ja käyttäjä tarkentaa haun tarkalle päivälle,
  // pelkkä vanhan AI-tulosjoukon näyttäminen ei riitä: kuukausihaun tuloksissa
  // voi olla esiintyjiä, jotka ovat vapaita jollain toisella kuukauden päivällä.
  //
  // Haetaan siksi /bands/free valitulle päivälle ja käytetään sen palauttamia
  // id/slug-arvoja faktapohjaisena saatavuusrajauksena AI-tulosten päällä.
  useEffect(() => {
    const exactDate =
      selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
        ? selectedDate
        : "";

    const requestId =
      aiExactDateAvailabilityRequestRef.current + 1;

    aiExactDateAvailabilityRequestRef.current = requestId;

    if (!aiSearchActive || !exactDate) {
      setAiExactDateAvailability((previous) => {
        if (!previous.date && previous.keys === null) {
          return previous;
        }

        return {
          date: "",
          keys: null,
        };
      });

      return;
    }

    const controller = new AbortController();

    // Tyhjennä aiemman päivän ratkaistu rajaus heti, ettei sitä käytetä
    // vahingossa uuden päivämäärän kanssa ennen uuden pyynnön valmistumista.
    setAiExactDateAvailability({
      date: exactDate,
      keys: null,
    });

    (async () => {
      try {
        const rawArtists = await api.freeBands(exactDate, {
          ...(regionForApi ? { region: regionForApi } : {}),
          signal: controller.signal,
        });

        if (
          aiExactDateAvailabilityRequestRef.current !== requestId
        ) {
          return;
        }

        const freeArtists = Array.isArray(rawArtists)
          ? rawArtists
          : rawArtists?.bands ?? [];

        const availableKeys = new Set();

        freeArtists.forEach((item) => {
          if (item?.id != null) {
            availableKeys.add(`id:${String(item.id)}`);
          }

          if (item?.slug) {
            availableKeys.add(`slug:${String(item.slug)}`);
          }
        });

        setAiExactDateAvailability({
          date: exactDate,
          keys: [...availableKeys],
        });
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }

        console.error(
          "AI-haun tarkan päivän saatavuuden tarkistus epäonnistui:",
          error
        );

        if (
          aiExactDateAvailabilityRequestRef.current === requestId
        ) {
          // Fail closed: jos päivän saatavuutta ei pystytä varmistamaan,
          // älä näytä vanhaa kuukausitulosta varmana tälle päivälle.
          setAiExactDateAvailability({
            date: exactDate,
            keys: [],
          });

          setAiSearchError(
            "Valitun päivän saatavuutta ei voitu tarkistaa juuri nyt. Yritä uudelleen."
          );
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [aiSearchActive, selectedDate, regionForApi]);

useEffect(() => {
  const params = getSearchParamsFromRaw(rrLocation.search);
  const urlState = readHomeStateFromSearch(rrLocation.search);
  const wantsBooking = urlState.wantsBooking;
  const fromState = rrLocation.state?.openBooking;
  // scrollTo voi tulla ilman #-merkkiä → normalisoidaan
  const rawTarget = rrLocation.state?.scrollTo || urlState.scrollTo || RESULTS_ID;
  const target = rawTarget.startsWith("#") ? rawTarget : `#${rawTarget}`;

  // Jos URL tai state pyytää avaamaan haun → avaa pikahakumodali ja scrollaa kohteeseen.
  if (wantsBooking || fromState) {
    setShowBooking(true);

    requestAnimationFrame(() => {
      document.querySelector(target)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    // siivoa URL/state ettei jää looppaamaan
    params.delete("book");
    params.delete("scrollTo");
    navigate(
      { pathname: rrLocation.pathname, search: params.toString() },
      { replace: true, state: {} }
    );
  }
}, [rrLocation.pathname, rrLocation.search, rrLocation.state, navigate]);

  // ---------- Efekti: pelkkä scroll-to ----------
  useEffect(() => {
    if (rrLocation.state?.scrollTo !== RESULTS_ID || scrolledOnce) return;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(RESULTS_ID);
      if (el || tries > 40) {
        clearInterval(timer);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setScrolledOnce(true);
        navigate(rrLocation.pathname + rrLocation.search + rrLocation.hash, {
          replace: true,
          state: {},
        });
      }
      tries += 1;
    }, 50);
    return () => clearInterval(timer);
  }, [
    rrLocation.pathname,
    rrLocation.search,
    rrLocation.hash,
    rrLocation.state,
    navigate,
    scrolledOnce,
  ]);

  // Genret (DB → dedupe case-insensitiivisesti + aakkosjärjestys)
// Genret (DB → normalisointi + aakkosjärjestys)
useEffect(() => {
  const ac = new AbortController();
  (async () => {
    try {
      const raw = await api.genres({ signal: ac.signal });
      setAvailableGenres(normalizeGenreNames(raw));
    } catch (e) {
      if (e.name !== "AbortError") console.error("Genrejen haku epäonnistui:", e);
    } finally {
      setGenresReady(true);
    }
  })();
  return () => ac.abort();
}, []);

// ---------- API: meta (artist types) ----------
useEffect(() => {
  setTypesReady(true);
}, []);

// ---------- API haut (lista) ----------
useEffect(() => {
  // Odota, että URL → state -alkusynkka on valmis.
  // Näin myös URL:sta tulevat alue- ja muut suodattimet ehtivät synkronoitua.
  if (!initialSyncDone) return;
  const ac = new AbortController();

  (async () => {
    try {
      // Hae kaikki julkaistut esiintyjät.
      // Kalenterissa olevien vapaiden päivien puuttuminen ei piilota profiilia.
      const artistsPromise = api.bands({
        signal: ac.signal,
        ...(regionForApi ? { region: regionForApi } : {}),
      });

      // Yritykset (jos käytössä): samoin vain ne, joilla valittu päivä on auki
      const companiesPromise = SHOW_COMPANIES
        ? api.freeCompanies(selectedDate || todayLocalISO(), { signal: ac.signal })
        : Promise.resolve([]);

      const [artistsRes, companiesRes] = await Promise.allSettled([
        artistsPromise,
        companiesPromise,
      ]);

      const artistsRaw =
        artistsRes.status === "fulfilled" ? artistsRes.value : [];
      const companiesRaw =
        companiesRes.status === "fulfilled" ? companiesRes.value : [];

      // normalisoinnit (käyttävät normalizeArtist / normalizeCompany)
      const artists = (Array.isArray(artistsRaw) ? artistsRaw : artistsRaw?.bands ?? [])
        .map(normalizeArtist);

      const companies = SHOW_COMPANIES
        ? (Array.isArray(companiesRaw) ? companiesRaw : companiesRaw?.companies ?? [])
            .map(normalizeCompany)
        : [];

      // 1) Yhdistä
      const combined = SHOW_COMPANIES ? [...companies, ...artists] : [...artists];

      // Säilytä backendin antama järjestys.
      // Hintalajittelu tehdään vain, jos käyttäjä valitsee sen erikseen.
      setBands(combined);

      // Päivitä palvelut (vain jos yritykset mukana)
      if (SHOW_COMPANIES) {
        const servicesFlat = companies.flatMap((c) =>
          Array.isArray(c.services)
            ? c.services
            : typeof c.services === "string"
            ? c.services.split(",")
            : []
        );
        const uniqServices = [...new Set(servicesFlat.map((s) => String(s).trim()).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, "fi", { sensitivity: "base" }));
        setAvailableServices(uniqServices);
      } else {
        setAvailableServices([]);
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error("Virhe listauksessa:", e);
      }
      } finally {
        setListReady(true);
      }
  })();

  return () => ac.abort();
// debouncedSearchTerm poistuu riippuvuuksista: haku on client-side filtteri,
// eikä sen pidä triggeröidä uutta verkkohakua jokaisella kirjoituksella.
}, [initialSyncDone, selectedDate, regionForApi]);

  // Alueet
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const raw = await api.regions({ signal: ac.signal });
        const names = (Array.isArray(raw) ? raw : [])
          .map((r) => (typeof r === "string" ? r : r?.name || r?.region || ""))
          .filter(Boolean);
        const uniqueSorted = [...new Set(names)].sort((a, b) =>
          a.localeCompare(b, "fi")
        );
        setAvailableRegions(uniqueSorted);
      } catch (e) {
        if (e.name !== "AbortError")
          console.error("Alueiden haku epäonnistui:", e);
        } finally {
        setRegionsReady(true);
      }
    })();
    return () => ac.abort();
  }, []);

  const searchableBands = useMemo(() => {
    return (bands || []).map((band) => {
      const nameText = lower([
        band?.name,
        band?.band_name,
        band?.artist_name,
        band?.artist_title,
        band?.profile_name,
        band?.title,
        band?.display_name,
        band?.slug?.replace(/[-_]+/g, " "),
      ]);

      const bandTags =
        band.__kind === "company"
          ? (Array.isArray(band.services) ? band.services : []).map(lower)
          : extractGenres(band).map(lower);

      const bandPrograms = extractPrograms(band).map(lower);
      const bandLanguages = extractLanguages(band).map(lower);
      const bandHasOwnStudio = hasOwnStudioFacility(band);
      const startingPriceN = parseItemPrice(band);
      const typeVariants = getNormalizedTypeVariants(
        band?.artist_type ?? band?.artist_type_name ?? "",
        artistTypeLabelToKey
      );

      return {
        raw: band,
        nameText,
        bandTags,
        bandPrograms,
        bandLanguages,
        bandHasOwnStudio,
        startingPriceN,
        typeVariants,
      };
    });
  }, [bands, artistTypeLabelToKey]);

    const filterBandsForCurrentCriteria = useCallback((sourceBands = []) => {
    const qName = lower(debouncedSearchTerm || "");
    const qLoc  = lower(debouncedLocation || "");
    const qLocRegion = lower(getRegionForMunicipality(debouncedLocation || ""));
    const selTypes   = selectedArtistTypes.map((t) => lower(t));
    const selectedTypeVariants = selTypes.flatMap((t) =>
      getNormalizedTypeVariants(t, artistTypeLabelToKey)
    );

    const selTags = tagsDisabled
      ? []
      : (isCompanyMode
          ? selectedServices
          : (isMusicTypeSelected ? selectedGenres : [])
        ).map(lower);

    const selPrograms = (tagsDisabled || isCompanyMode || isMusicTypeSelected || isPhotographerTypeSelected)
      ? []
      : selectedPrograms.map(lower);

    const selLanguages = (tagsDisabled || isCompanyMode || isMusicTypeSelected || isPhotographerTypeSelected)
      ? []
      : selectedLanguages.map(lower);

    const selPhotoFacilities = (tagsDisabled || isCompanyMode || !isPhotographerTypeSelected)
      ? []
      : selectedPhotoFacilities.map(lower);

    const minN = parseMoneyFi(priceMin);
    const maxN = parseMoneyFi(priceMax);
    const bucket = String(selectedPriceBucket || "").trim();

    const entries = (sourceBands || []).map((band) => {
      const nameText = lower([
        band?.name,
        band?.band_name,
        band?.artist_name,
        band?.artist_title,
        band?.profile_name,
        band?.title,
        band?.display_name,
        band?.slug?.replace(/[-_]+/g, " "),
      ]);

      const bandTags =
        band.__kind === "company"
          ? (Array.isArray(band.services) ? band.services : []).map(lower)
          : extractGenres(band).map(lower);

      const bandPrograms = extractPrograms(band).map(lower);
      const bandLanguages = extractLanguages(band).map(lower);
      const bandHasOwnStudio = hasOwnStudioFacility(band);
      const startingPriceN = parseItemPrice(band);
      const typeVariants = getNormalizedTypeVariants(
        band?.artist_type ?? band?.artist_type_name ?? "",
        artistTypeLabelToKey
      );

      return {
        raw: band,
        nameText,
        bandTags,
        bandPrograms,
        bandLanguages,
        bandHasOwnStudio,
        startingPriceN,
        typeVariants,
      };
    });

    return entries
      .filter(({ raw }) => (SHOW_COMPANIES ? true : raw.__kind !== "company"))
      .filter((entry) => {
        const {
          nameText,
          bandTags,
            bandPrograms,
          bandLanguages,
          bandHasOwnStudio,
          startingPriceN,
          typeVariants,
        } = entry;

        const matchName = !qName || nameText.includes(qName);
        // Aluerajaus tehdään jo backendissä regionForApi-parametrilla.
        // Tapahtumapaikkakunnasta tarkistetaan tässä vain, että se tunnetaan
        // ja siitä saatiin maakunta. Älä suodata API:n tuloksia uudelleen
        // profiilin bandRegions-kentän perusteella.
        const matchLocation = !qLoc || !!qLocRegion;
        const matchType =
          selectedTypeVariants.length === 0 ||
          selectedTypeVariants.some((variant) => typeVariants.includes(variant));
        const matchTags =
          selTags.length === 0 || selTags.some((tg) => bandTags.includes(tg));

        const matchPrograms =
          selPrograms.length === 0 || selPrograms.some((pr) => bandPrograms.includes(pr));

        const matchLanguages =
          selLanguages.length === 0 ||
          selLanguages.some((lng) => bandLanguages.includes(String(lng).toLowerCase()));

        const matchPhotoFacilities =
          selPhotoFacilities.length === 0 ||
          selPhotoFacilities.every((f) => {
            if (f === "oma studio") return bandHasOwnStudio;
            return true;
          });

        const matchPrice =
          (!bucket && minN == null && maxN == null)
            ? true
            : (
                matchesPriceBucket(startingPriceN, bucket) &&
                (minN == null || (startingPriceN != null && startingPriceN >= minN)) &&
                (maxN == null || (startingPriceN != null && startingPriceN <= maxN))
              );

        return matchName && matchLocation && matchType &&matchTags && matchPrograms && matchLanguages && matchPhotoFacilities && matchPrice;
      })
      .map((entry) => entry.raw)
      .sort((a, b) => compareItemsByPriceAndName(a, b, priceSort));
  }, [
    debouncedSearchTerm,
    debouncedLocation,
    selectedArtistTypes,
    selectedServices,
    selectedGenres,
      selectedPrograms,
    selectedLanguages,
    selectedPhotoFacilities,
    selectedPriceBucket,
    priceMin,
    priceMax,
    priceSort,
    artistTypeLabelToKey,
    isCompanyMode,
    isMusicTypeSelected,
    isPhotographerTypeSelected,
    tagsDisabled,
  ]);

  const filteredBands = useMemo(() => {
  const qName = lower(debouncedSearchTerm || "");
  const qLoc  = lower(debouncedLocation || "");
  const qLocRegion = lower(getRegionForMunicipality(debouncedLocation || ""));
  const selTypes   = selectedArtistTypes.map((t) => lower(t));
  const selectedTypeVariants = selTypes.flatMap((t) =>
    getNormalizedTypeVariants(t, artistTypeLabelToKey)
  );

    // jos tagit disabloitu (Kaikki), ei rajata lisäsuodattimilla
    const selTags = tagsDisabled
      ? []
      : (isCompanyMode
          ? selectedServices
          : (isMusicTypeSelected ? selectedGenres : [])
        ).map(lower);

    // Tilaisuudet: annetaan toimia myös musiikkityypeillä (ja vaikka tyyppiä ei olisi valittu)

    const selPrograms = (tagsDisabled || isCompanyMode || isMusicTypeSelected || isPhotographerTypeSelected)
      ? []
      : selectedPrograms.map(lower);

     // Kielet (ei-musiikkityypeille)
     const selLanguages = (tagsDisabled || isCompanyMode || isMusicTypeSelected || isPhotographerTypeSelected)
       ? []
       : selectedLanguages.map(lower);

     // Valokuvaajan puitteet
     const selPhotoFacilities = (tagsDisabled || isCompanyMode || !isPhotographerTypeSelected)
       ? []
       : selectedPhotoFacilities.map(lower);

     // Budjetti (toimii kaikille tyypeille)
     const minN = parseMoneyFi(priceMin);
     const maxN = parseMoneyFi(priceMax);
     const bucket = String(selectedPriceBucket || "").trim();

    return searchableBands
      .filter(({ raw }) => (SHOW_COMPANIES ? true : raw.__kind !== "company"))
      .filter((entry) => {
        const {
          nameText,
          bandTags,
            bandPrograms,
          bandLanguages,
          bandHasOwnStudio,
          startingPriceN,
          typeVariants,
        } = entry;

        const matchName = !qName || nameText.includes(qName);

        // Aluerajaus tehdään backendissä regionForApi-parametrilla.
        // Paikkakunnan pitää vain olla tunnettu kunta, josta saadaan maakunta.
        const matchLocation = !qLoc || !!qLocRegion;
        const matchType =
          selectedTypeVariants.length === 0 ||
          selectedTypeVariants.some((variant) => typeVariants.includes(variant));
        const matchTags =
          selTags.length === 0 || selTags.some((tg) => bandTags.includes(tg));
          const matchPrograms =
          selPrograms.length === 0 || selPrograms.some((pr) => bandPrograms.includes(pr));

          const matchLanguages =
           selLanguages.length === 0 ||
           selLanguages.some((lng) => bandLanguages.includes(String(lng).toLowerCase()));

           const matchPhotoFacilities =
           selPhotoFacilities.length === 0 ||
           selPhotoFacilities.every((f) => {
             if (f === "oma studio") return bandHasOwnStudio;
             return true;
           });

         const matchPrice =
           (!bucket && minN == null && maxN == null)
             ? true
             : (
                 matchesPriceBucket(startingPriceN, bucket) &&
                 (minN == null || (startingPriceN != null && startingPriceN >= minN)) &&
                 (maxN == null || (startingPriceN != null && startingPriceN <= maxN))
               );

         return matchName && matchLocation && matchType &&matchTags && matchPrograms && matchLanguages && matchPhotoFacilities && matchPrice;
      })
      .map((entry) => entry.raw);
  }, [
    searchableBands,
    debouncedSearchTerm,
    artistTypeLabelToKey,
    debouncedLocation,
    selectedArtistTypes,
    selectedGenres,
    selectedServices,
      selectedPrograms,
     selectedLanguages,
     selectedPhotoFacilities,
     selectedPriceBucket,
     priceMin,
     priceMax,
    isCompanyMode,
    tagsDisabled,
    isMusicTypeSelected,
    isPhotographerTypeSelected,
  ]);

// ✅ Lopullinen listaus:
// - normaali Suositellut = laatupisteet + monipuolinen marketplace-näkymä
// - AI-haun Suositellut = AI:n recommendation_score
// - Halvin / Kallein = hinta
const displayedBands = useMemo(() => {
  // AI-haun jälkeen manuaaliset suodattimet toimivat tarkennuksina
  // nykyiseen AI-tulosjoukkoon. Normaalihaussa käytetään tavallista
  // filteredBands-listaa kuten ennenkin.
  const aiFilteredByManualCriteria = aiSearchActive
    ? filterBandsForCurrentCriteria(aiSearchResults)
    : [];

  const aiFilteredByRegion = aiSearchActive
    ? aiFilteredByManualCriteria.filter((item) => {
        if (!selectedRegions.length) return true;

        const regionText = [
          ...toStringArray(item?.regions),
          ...toStringArray(item?.performance_regions),
          String(item?.region_names || ""),
        ]
          .join(" ")
          .toLocaleLowerCase("fi")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");

        return selectedRegions.some((region) => {
          const regionKey = String(region || "")
            .trim()
            .toLocaleLowerCase("fi")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

          return regionKey && regionText.includes(regionKey);
        });
      })
    : [];

  const hasResolvedAiExactDateAvailability =
    aiSearchActive &&
    Boolean(
      selectedDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
    ) &&
    aiExactDateAvailability.date === selectedDate &&
    Array.isArray(aiExactDateAvailability.keys);

  const aiExactDateAvailableKeySet =
    hasResolvedAiExactDateAvailability
      ? new Set(aiExactDateAvailability.keys)
      : null;

  const aiFilteredByExactDate =
    aiSearchActive && aiExactDateAvailableKeySet
      ? aiFilteredByRegion.filter((item) => {
          const itemKeys = [];

          if (item?.id != null) {
            itemKeys.push(`id:${String(item.id)}`);
          }

          if (item?.slug) {
            itemKeys.push(`slug:${String(item.slug)}`);
          }

          return itemKeys.some((key) =>
            aiExactDateAvailableKeySet.has(key)
          );
        })
      : aiFilteredByRegion;

  const source = aiSearchActive
    ? aiFilteredByExactDate
    : filteredBands;

  const bookabilityDate =
    selectedDateSource === "default" ? "" : selectedDate;

  const list = [...(source || [])].filter((item) =>
    isBookableByBuffer(item, bookabilityDate)
  );

// =====================================================
// SMART SEARCH
// =====================================================
// 1. Hakurelevanssi ratkaisee ensin.
// 2. Jos relevanssi on sama, parempi profiilin laatupiste voittaa.
// 3. Jos molemmat ovat samat, järjestetään nimen mukaan.
  if (aiSearchActive && priceSort === "recommended") {
    return list.sort((a, b) => {
      const relevanceDiff =
        Number(b?.recommendation_rank_score || 0) -
        Number(a?.recommendation_rank_score || 0);

      if (relevanceDiff !== 0) return relevanceDiff;

      const qualityDiff =
        Number(b?.profile_quality_score || 0) -
        Number(a?.profile_quality_score || 0);

      if (qualityDiff !== 0) return qualityDiff;

      return String(a?.name || "").localeCompare(
        String(b?.name || ""),
        "fi",
        { sensitivity: "base" }
      );
    });
  }

// =====================================================
// NORMAALI SUOSITELLUT
// =====================================================
if (!aiSearchActive && priceSort === "recommended") {

  // -----------------------------------------------------
  // Tekijätyyppi marketplace-diversiteettiä varten
  // -----------------------------------------------------
  const getCategoryKey = (item) => {
    const type = String(
      item?.artist_type || ""
    )
      .trim()
      .toLocaleLowerCase("fi");

    if (
      type.includes("bilebänd") ||
      type.includes("bileband")
    ) {
      return "bilebandi";
    }

    if (
      type.includes("oma musiikki") ||
      type === "bändi" ||
      type === "bandi" ||
      type === "band"
    ) {
      return "bandi";
    }

    if (
      type === "dj" ||
      type.startsWith("dj ")
    ) {
      return "dj";
    }

    if (
      type.includes("sirkus") ||
      type.includes("circus") ||
      type.includes("akrobat")
    ) {
      return "sirkus_show";
    }

    if (type.includes("valokuva")) {
      return "valokuvaaja";
    }

    if (type.includes("juont")) {
      return "juontaja";
    }

    if (
      type.includes("standup") ||
      type.includes("stand-up") ||
      type.includes("koomikko")
    ) {
      return "standup";
    }

    if (type.includes("trubaduur")) {
      return "trubaduuri";
    }

    if (
      type === "duo" ||
      type.includes("duo")
    ) {
      return "duo";
    }

    if (type.includes("soolo")) {
      return "sooloartisti";
    }

    return type || `other-${item?.id}`;
  };

  // -----------------------------------------------------
  // Puhdas laatupistejärjestys
  // -----------------------------------------------------
// -----------------------------------------------------
// Puhdas laatupistejärjestys
// -----------------------------------------------------
const ranked = list
  .map((item, originalIndex) => ({
    item,
    originalIndex,
    score: Number(item?.profile_quality_score || 0),
    category: getCategoryKey(item),
  }))
  .sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }

    const reviewsA = Number(
      a.item?.reviews_count || 0
    );

    const reviewsB = Number(
      b.item?.reviews_count || 0
    );

    if (reviewsB !== reviewsA) {
      return reviewsB - reviewsA;
    }

    return String(
      a.item?.name || ""
    ).localeCompare(
      String(b.item?.name || ""),
      "fi",
      { sensitivity: "base" }
    );
  });

  // -----------------------------------------------------
  // Jos käyttäjä valitsi yhden tekijätyypin,
  // ei tehdä marketplace-diversiteettiä.
  // -----------------------------------------------------
  if (selectedArtistTypes.length > 0) {
    return ranked.map(
      (entry) => entry.item
    );
  }

  // =====================================================
  // KAIKKI-TILA
  // =====================================================

  const TOP_COUNT = 6;
  const MAX_MUSIC_IN_TOP = 3;

  const MUSIC_CATEGORIES = new Set([
    "bilebandi",
    "bandi",
    "dj",
    "trubaduuri",
    "duo",
    "sooloartisti",
  ]);

  const isMusicCategory = (category) =>
    MUSIC_CATEGORIES.has(category);

  const selectedTop = [];
  const usedIds = new Set();
  const usedCategories = new Set();

  const getEntryId = (entry) =>
    String(
      entry?.item?.id ??
        entry?.item?.slug ??
        entry?.originalIndex
    );

  const addEntry = (entry) => {
    if (!entry) {
      return false;
    }

    const idKey = getEntryId(entry);

    if (usedIds.has(idKey)) {
      return false;
    }

    selectedTop.push(entry);
    usedIds.add(idKey);
    usedCategories.add(entry.category);

    return true;
  };

  // -----------------------------------------------------
  // 1. Paras taikuri mukaan, jos sellainen löytyy.
  // -----------------------------------------------------
  const bestMagician = ranked.find(
    (entry) =>
      entry.category === "taikuri"
  );

  addEntry(bestMagician);

  // -----------------------------------------------------
  // 2. Paras valokuvaaja mukaan, jos sellainen löytyy.
  // -----------------------------------------------------
  const bestPhotographer = ranked.find(
    (entry) =>
      entry.category === "valokuvaaja"
  );

  addEntry(bestPhotographer);

  // -----------------------------------------------------
  // 3. Täytetään eri tekijätyypeillä.
  //
  // - sama tarkka tyyppi vain kerran
  // - musiikkitoimijoita enintään 3
  // -----------------------------------------------------
  for (const entry of ranked) {
    if (selectedTop.length >= TOP_COUNT) {
      break;
    }

    if (
      usedIds.has(getEntryId(entry))
    ) {
      continue;
    }

    if (
      usedCategories.has(entry.category)
    ) {
      continue;
    }

    if (
      isMusicCategory(entry.category)
    ) {
      const musicCount =
        selectedTop.filter(
          (selected) =>
            isMusicCategory(
              selected.category
            )
        ).length;

      if (
        musicCount >= MAX_MUSIC_IN_TOP
      ) {
        continue;
      }
    }

    addEntry(entry);
  }

  // -----------------------------------------------------
  // 4. Jos kuutta eri tyyppiä ei löydy,
  // sallitaan saman tyypin toinen profiili.
  //
  // Musiikin max 3 säilyy edelleen.
  // -----------------------------------------------------
  for (const entry of ranked) {
    if (selectedTop.length >= TOP_COUNT) {
      break;
    }

    if (
      usedIds.has(getEntryId(entry))
    ) {
      continue;
    }

    if (
      isMusicCategory(entry.category)
    ) {
      const musicCount =
        selectedTop.filter(
          (selected) =>
            isMusicCategory(
              selected.category
            )
        ).length;

      if (
        musicCount >= MAX_MUSIC_IN_TOP
      ) {
        continue;
      }
    }

    addEntry(entry);
  }

  // -----------------------------------------------------
  // 5. Viimeinen turvaverkko:
  // jos TOP 6 ei vieläkään täyty.
  // -----------------------------------------------------
  for (const entry of ranked) {
    if (selectedTop.length >= TOP_COUNT) {
      break;
    }

    if (
      usedIds.has(getEntryId(entry))
    ) {
      continue;
    }

    addEntry(entry);
  }

  // -----------------------------------------------------
  // 6. TOP 6 takaisin keskinäiseen laatupistejärjestykseen.
  // -----------------------------------------------------
  const rankPosition = new Map(
    ranked.map((entry, index) => [
      getEntryId(entry),
      index,
    ])
  );

  selectedTop.sort((a, b) => {
    const posA =
      rankPosition.get(getEntryId(a)) ??
      Number.MAX_SAFE_INTEGER;

    const posB =
      rankPosition.get(getEntryId(b)) ??
      Number.MAX_SAFE_INTEGER;

    return posA - posB;
  });

  // -----------------------------------------------------
  // 7. Loput normaalissa laatupistejärjestyksessä.
  // -----------------------------------------------------
  const rest = ranked.filter(
    (entry) =>
      !usedIds.has(getEntryId(entry))
  );

  return [
    ...selectedTop,
    ...rest,
  ].map((entry) => entry.item);
}

  // =====================================================
  // HALVIN / KALLEIN
  // =====================================================
  list.sort((a, b) =>
    compareItemsByPriceAndName(
      a,
      b,
      priceSort
    )
  );

  return list;
}, [
  filteredBands,
  aiSearchActive,
  aiSearchResults,
  aiExactDateAvailability,
  filterBandsForCurrentCriteria,
  selectedRegions,
  priceSort,
  selectedArtistTypes,
  selectedDate,
  selectedDateSource,
]);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [displayedBands, pageSize]);

  const activeFiltersCount = useMemo(
    () =>
      (debouncedSearchTerm ? 1 : 0) +
      (debouncedLocation ? 1 : 0) +
      selectedRegions.length +
      selectedArtistTypes.length +
       (selectedPriceBucket ? 1 : 0) +
       (parseMoneyFi(priceMin) != null ? 1 : 0) +
       (parseMoneyFi(priceMax) != null ? 1 : 0) +
       (priceSort !== "recommended" ? 1 : 0) +
      (tagsDisabled
        ? 0
        : isCompanyMode
        ? selectedServices.length
        : isMusicTypeSelected
        ? selectedGenres.length
        : isPhotographerTypeSelected
        ? selectedPhotoFacilities.length
        : (selectedPrograms.length + selectedLanguages.length)),
    [
      debouncedSearchTerm,
      debouncedLocation,
      selectedRegions.length,
      selectedArtistTypes.length,
       selectedPriceBucket,
       priceMin,
       priceMax,
       priceSort,
      isCompanyMode,
      selectedServices.length,
      selectedGenres.length,
      selectedPrograms.length,
      selectedLanguages.length,
      selectedPhotoFacilities.length,
      tagsDisabled,
      isPhotographerTypeSelected,
      isMusicTypeSelected,
    ]
  );

  // ---------- UI osia ----------
const labelBase =
  "block font-customFont1 text-sm xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs md:text-[13px] md:font-semibold uppercase tracking-[0.06em] text-black/95 dark:text-white/90 mb-1 dark:text-shadow-xxl";
    const labelSmall =
    "block font-customFont1 text-[11px] uppercase tracking-wide text-black/70 dark:text-white/70 mb-1 dark:text-shadow-xxl";

  const inputBase =
    "w-full h-10 rounded-lg px-3 font-customFont1 bg-[#111] text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-primary dark:text-shadow-xxl text-sm xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs";

const artistTypesUI = useMemo(() => {
  const labels = Object.values(ARTIST_TYPES || {})
    .map((item) => String(item?.label || "").trim())
    .filter(Boolean)
    .filter((label) => SHOW_COMPANIES || label.toLowerCase() !== "yritys");

  const uniqueLabels = [...new Set(labels)];

  return uniqueLabels.sort((a, b) => {
    const indexA = ARTIST_TYPE_ORDER.indexOf(a);
    const indexB = ARTIST_TYPE_ORDER.indexOf(b);

    const rankA = indexA === -1 ? 999 : indexA;
    const rankB = indexB === -1 ? 999 : indexB;

    if (rankA !== rankB) return rankA - rankB;

    return a.localeCompare(b, "fi", { sensitivity: "base" });
  });
}, []);

  const artistTypeOptions = useMemo(() => {
  const selected = selectedArtistTypes[0] || "";
  if (!selected) return artistTypesUI;
  return artistTypesUI.includes(selected)
    ? artistTypesUI
    : [selected, ...artistTypesUI];
}, [selectedArtistTypes, artistTypesUI]);

// Enää EI yhdistetä mihinkään kovakoodattuun – vain DB
const genresUI = useMemo(() => availableGenres, [availableGenres]);

  const regionsUI = useMemo(() => availableRegions, [availableRegions]);

  // ✅ Desktop: rajaa pikachipit vain niihin arvoihin, jotka oikeasti löytyy listoista
  const quickArtistTypeChips = useMemo(
    () => QUICK_ARTIST_TYPES.filter((t) => artistTypesUI.includes(t)),
    [artistTypesUI]
  );

  const clearFilters = () => {
    // Täysnollaus = sama lähtötila kuin tuoreella etusivulla.
    // Tyhjennetään sekä tavalliset suodattimet että AI-haun oma tila,
    // ettei month/range/exact-intentti tai vanha AI-tulosjoukko jää elämään.
    const defaultDate = todayLocalISO();

    setSearchTerm("");
    setLocationFilter("");
    setSelectedArtistTypes([]);
    resetDetailFilters();
    clearPriceFilters();
    setSelectedRegions([]);
    setShowMoreFilters(false);

    setAiQuery("");
    setAiSearchResults([]);
    setAiSearchActive(false);
    setAiDateRange(null);
    setAiSearchError("");
    setAiExactDateAvailability({
      date: "",
      keys: null,
    });

    // Mitätöi mahdollinen vielä kesken oleva tarkan päivän saatavuuspyyntö.
    aiExactDateAvailabilityRequestRef.current += 1;

    // Nollaa myös mahdolliset "etsi seuraava vapaa päivä" -tulokset.
    setNextAvailableDays([]);
    setNextAvailableSearched(false);

    // Palauta päivämäärä etusivun normaaliin oletukseen.
    // Suojaa uusi defaultDate hetken vanhalta URL-päivältä siihen asti,
    // että state -> URL -synkka ehtii päivittää query-parametrin.
    pendingDateUrlSyncRef.current = defaultDate;
    setSelectedDate(defaultDate);
    setSelectedDateSource("default");

    try {
      sessionStorage.removeItem(HOME_FILTERS_KEY);
      sessionStorage.removeItem(HOME_AI_RETURN_KEY);

      // Jos käyttäjä nollaa haun samassa mountissa, myös jo luettu paluusnapshot
      // mitätöidään, ettei sitä käsitellä myöhemmin vanhana totuutena.
      homeAiReturnStateRef.current = null;
    } catch {}

    try {
      localStorage.removeItem(DATE_KEY);
      localStorage.removeItem(REGIONS_KEY);
    } catch {}

    // live-haussa ei enää hasSearched-tilaa
    // (halutessa voi scrollata tuloksiin):
    // document.getElementById(RESULTS_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAiSearch = async () => {
  const query = aiQuery.trim();

  if (query.length < 3 || aiSearchLoading) {
    return;
  }

  setAiSearchLoading(true);
  setAiSearchError("");

  try {
    // Tekstissä annettu tarkka päivämäärä voittaa aina.
    // Muussa tapauksessa AI-haulle välitetään vanha päivämäärä vain,
    // jos käyttäjä on oikeasti valinnut sen tässä käyttöliittymässä.
    // localStorage/default-päivä EI saa rajata uutta AI-hakua huomaamatta.
    const explicitQueryDate = extractExplicitDateFromAiQuery(query);

    const canReuseSelectedDate =
      Boolean(
        selectedDate &&
        /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
      ) &&
      selectedDateSource === "user";

    const firstSearchDate =
      explicitQueryDate ||
      (canReuseSelectedDate ? selectedDate : "");

    const data = await api.aiSearch(query, {
      limit: 10,
      date: firstSearchDate || null,
    });

    const parsedDateMode = String(
      data?.parsed?.date_mode ||
      data?.parsed?.ai_intent?.date_mode ||
      (data?.parsed?.date ? "exact" : "none")
    )
      .trim()
      .toLowerCase();

    const parsedExactDate = String(
      data?.parsed?.date ||
      data?.parsed?.ai_intent?.date ||
      ""
    ).trim();

    const parsedDateFrom = String(
      data?.parsed?.date_from || ""
    ).trim();

    const parsedDateTo = String(
      data?.parsed?.date_to || ""
    ).trim();

    const hasParsedExactDate =
      parsedDateMode === "exact" &&
      /^\d{4}-\d{2}-\d{2}$/.test(parsedExactDate);

    const hasParsedDateRange =
      (parsedDateMode === "month" || parsedDateMode === "range") &&
      /^\d{4}-\d{2}-\d{2}$/.test(parsedDateFrom) &&
      /^\d{4}-\d{2}-\d{2}$/.test(parsedDateTo);

    if (hasParsedExactDate) {
      // AI:n tarkka päivä on nyt staten totuus. Suojaa sitä hetken vanhalta URL:lta,
      // kunnes state -> URL -synkka ehtii kirjoittaa saman päivän query-parametriin.
      pendingDateUrlSyncRef.current = parsedExactDate;
      setSelectedDate(parsedExactDate);
      setSelectedDateSource("ai");
      setAiDateRange(null);
    } else if (hasParsedDateRange) {
      // Kuukausi-/range-haku EI saa näyttää yhtä tekaistua yksittäistä päivää.
      // Tyhjä päivämäärä on tässä tarkoituksellinen state-arvo, joten suojaa sitä
      // vanhalta URL-päivältä siihen asti, että ?date poistuu URL:sta.
      pendingDateUrlSyncRef.current = "";
      setSelectedDate("");
      setSelectedDateSource("ai-range");
      setAiDateRange({
        mode: parsedDateMode,
        from: parsedDateFrom,
        to: parsedDateTo,
      });

      try {
        localStorage.removeItem(DATE_KEY);
      } catch {}
    } else {
      setAiDateRange(null);

      // Jos AI-haulla ei ole aikarajausta eikä käyttäjä itse valinnut päivää,
      // myös näkyvä stale/default-päivä poistetaan AI-haun ajaksi.
      if (!firstSearchDate) {
        pendingDateUrlSyncRef.current = "";
        setSelectedDate("");
        setSelectedDateSource("none");

        try {
          localStorage.removeItem(DATE_KEY);
        } catch {}
      }
    }

    // Synkkaa AI:n ymmärtämät hakuehdot näkyviin manuaalisiin
    // suodattimiin. Näin "Tarkenna hakua itse" jatkaa samaa hakua
    // eikä näytä käyttäjälle ristiriitaisia Kaikki-arvoja.
    const parsedIntent = data?.parsed?.ai_intent || {};
    const parsedLocation = data?.parsed?.location || {};

    const normalizeFilterValue = (value) =>
      stripAccents(
        String(value || "")
          .trim()
          .toLocaleLowerCase("fi")
      );

    // Uusi AI-haku on uusi hakuintentti. Edellisen AI-haun näkyviin
    // synkkaamat suodattimet eivät saa jäädä salaa rajaamaan seuraavan haun
    // tuloksia. Päivämäärä käsitellään erikseen yllä, koska käyttäjän itse
    // valitsema päivä saa toimia uuden AI-haun kontekstina.
    setSelectedArtistTypes([]);
    setLocationFilter("");
    setSelectedRegions([]);
    resetDetailFilters();
    clearPriceFilters();

    const parsedArtistType = String(
      data?.parsed?.artist_type || parsedIntent?.artist_type || ""
    ).trim();

    if (parsedArtistType) {
      const artistTypeKey = normalizeFilterValue(parsedArtistType);
      const matchedArtistType = artistTypesUI.find(
        (label) => normalizeFilterValue(label) === artistTypeKey
      );

      if (matchedArtistType) {
        setSelectedArtistTypes([matchedArtistType]);
      }
    }

    const parsedMunicipality = String(
      parsedLocation?.municipality || ""
    ).trim();

    if (parsedMunicipality) {
      setLocationFilter(parsedMunicipality);
    }

    const parsedRegion = String(
      parsedLocation?.region || ""
    ).trim();

    if (parsedRegion) {
      const regionKey = normalizeFilterValue(parsedRegion);
      const matchedRegion = availableRegions.find(
        (region) => normalizeFilterValue(region) === regionKey
      );

      setSelectedRegions([matchedRegion || parsedRegion]);
    }

    const parsedBudgetRaw =
      data?.parsed?.budget ?? parsedIntent?.budget_max;

    if (
      parsedBudgetRaw !== null &&
      parsedBudgetRaw !== undefined &&
      parsedBudgetRaw !== ""
    ) {
      const parsedBudgetMax = Number(parsedBudgetRaw);

      if (Number.isFinite(parsedBudgetMax) && parsedBudgetMax >= 0) {
        // Tarkka AI-budjetti näytetään MAX-kentässä.
        // Bucket jätetään "Kaikki"-tilaan, jotta esim. 1500 € ei muutu
        // epätarkaksi 1200–1799 € -haitariksi.
        setSelectedPriceBucket("");
        setPriceMin("");
        setPriceMax(String(Math.floor(parsedBudgetMax)));
      }
    }

    const parsedGenres = Array.isArray(parsedIntent?.genres)
      ? parsedIntent.genres
      : [];

    if (parsedGenres.length > 0 && availableGenres.length > 0) {
      const matchedGenres = parsedGenres
        .map((genre) => {
          const genreKey = normalizeFilterValue(genre);
          return availableGenres.find(
            (availableGenre) =>
              normalizeFilterValue(availableGenre) === genreKey
          );
        })
        .filter(Boolean);

      if (matchedGenres.length > 0) {
        setSelectedGenres([...new Set(matchedGenres)]);
      }
    }

const fullArtistById = new Map(
  (bands || [])
    .filter((item) => item?.__kind !== "company")
    .map((item) => [String(item.id), item])
);

const fullArtistBySlug = new Map(
  (bands || [])
    .filter((item) => item?.__kind !== "company" && item?.slug)
    .map((item) => [String(item.slug), item])
);

const items = Array.isArray(data?.items)
  ? data.items.map((aiItem) => {
      const fullArtist =
        fullArtistById.get(String(aiItem?.id)) ||
        fullArtistBySlug.get(String(aiItem?.slug)) ||
        {};

      return normalizeArtist({
        ...fullArtist,
        ...aiItem,

        // säilytetään varmasti AI-haun pisteytys
        recommendation_score: aiItem?.recommendation_score,
        recommendation_reasons: aiItem?.recommendation_reasons,
      });
    })
  : [];

    setAiSearchResults(items);
    setAiSearchActive(true);

    // AI:n pisteytys = Suositellut-järjestys.
    setPriceSort("recommended");

    requestAnimationFrame(() => {
      setTimeout(() => {
        scrollToResults();
      }, 80);
    });

    trackEvent("etusivu_ai_search", {
      result_count: items.length,
      mode: data?.mode || "unknown",
      ai_enabled: Boolean(data?.ai_enabled),
    });
  } catch (error) {
    console.error("AI-haku epäonnistui:", error);

    setAiSearchResults([]);
    setAiSearchActive(false);
    setAiDateRange(null);
    setAiSearchError(
      "Hakua ei voitu tehdä juuri nyt. Yritä uudelleen."
    );
  } finally {
    setAiSearchLoading(false);
  }
};

  const handleFindNextAvailableDays = useCallback(async () => {
    if (nextAvailableLoading) return;

    setNextAvailableSearched(true);
    setNextAvailableLoading(true);
    setNextAvailableDays([]);

    try {
      const found = [];
      const startDate = selectedDate || todayLocalISO();

      // Haetaan enintään seuraavat 14 päivää ja poimitaan niistä 3 ensimmäistä,
      // joilta löytyy osumia nykyisillä hakuehdoilla.
      for (let offset = 1; offset <= 14; offset += 1) {
        const nextDate = addDaysToIso(startDate, offset);

        const rawArtists = await api.freeBands(nextDate, {
          ...(regionForApi ? { region: regionForApi } : {}),
        });

        const normalizedArtists = (Array.isArray(rawArtists) ? rawArtists : rawArtists?.bands ?? [])
          .map(normalizeArtist);

        const filteredForDate = filterBandsForCurrentCriteria(normalizedArtists);

        if (filteredForDate.length > 0) {
          found.push({
            date: nextDate,
            count: filteredForDate.length,
          });
        }

        if (found.length >= 3) break;
      }

      setNextAvailableDays(found);
    } catch (error) {
      console.error("Seuraavien vapaiden päivien haku epäonnistui:", error);
      setNextAvailableDays([]);
    } finally {
      setNextAvailableLoading(false);
    }
  }, [
    nextAvailableLoading,
    selectedDate,
    regionForApi,
    addDaysToIso,
    filterBandsForCurrentCriteria,
  ]);

  // Laske määrät tyypeittäin (yrityksiä ei huomioida kun SHOW_COMPANIES=false)
const countByType = (items) =>
  items.reduce(
    (acc, it) => {
      if (SHOW_COMPANIES && it.__kind === "company") {
        acc.company++;
        return acc;
      }
      const t = String(it.artist_type || "").toLowerCase();
      if (t.includes("dj")) acc.dj++;
      else if (t.includes("bilebänd") || t.includes("bileband")) acc.bilebandi++;
      else if (t.includes("trub")) acc.trubaduuri++;
      else if (t.includes("duo")) acc.duo = (acc.duo || 0) + 1;
      else acc.other++;
      return acc;
    },
    { company: 0, dj: 0, bilebandi: 0, trubaduuri: 0, duo: 0, other: 0 }
  );

  // apurit suomen taivutukseen
  const formatCountFi = (n, key) => {
    // [nominatiivi_yks, partitiivi_yks]
    const forms = {
      toimija: ["toimija", "toimijaa"],
      tekijä: ["tekijä", "tekijää"],
      esiintyjä: ["esiintyjä", "esiintyjää"],
      yritys: ["yritys", "yritystä"],
    };
    const [sg, part] = forms[key] ?? [key, `${key}a`];
    return n === 1 ? `1 ${sg}` : `${n} ${part}`;
  };

const resultsLabels = useMemo(() => {
  const list = displayedBands;
  const total = list.length;

  const aiRangeText =
    aiSearchActive
      ? formatAiDateRangeFi(aiDateRange)
      : "";

  const dateText = aiRangeText
    || (
      selectedDate
        ? formatDateFi(selectedDate)
        : (
          aiSearchActive
            ? ""
            : formatDateFi(todayLocalISO())
        )
    );

  const dateSuffix =
    dateText
      ? ` · ${dateText}`
      : "";

  const availabilitySentence = aiRangeText
    ? `Haussa huomioitiin saatavuus ajalle ${aiRangeText}.`
    : selectedDate
      ? `Tarkista saatavuus päivälle ${formatDateFi(selectedDate)} tai kysy saatavuutta viestillä.`
      : "Tarkista saatavuus profiilista tai kysy saatavuutta viestillä.";

  const locationValue = String(debouncedLocation || "").trim();
  const regionValues = Array.isArray(debouncedRegions)
    ? debouncedRegions.filter(Boolean)
    : [];

  const scopeText = formatResultsScopeFi(
    locationValue,
    regionValues
  );

  const foundScopeText = locationValue
    ? `tapahtumapaikalle ${locationValue}`
    : regionValues.length === 1
      ? `alueelle ${regionValues[0]}`
      : regionValues.length > 1
        ? `alueille ${regionValues.join(", ")}`
        : "koko Suomesta";

  const hasAreaSelection =
    Boolean(locationValue || regionValues.length);

  const priceInfo = hasAreaSelection
    ? "Korttien alk. hinnat perustuvat valittuun alueeseen."
    : "Korteissa näkyy alin alk. hinta.";

  const mobilePriceInfo = hasAreaSelection
    ? "Alk. hinta valitulle alueelle"
    : "Alin alk. hinta";

  if (total === 0) {
    const noResults = `Hakuehdoilla ei löytynyt ${
      SHOW_COMPANIES ? "toimijoita" : "tekijöitä"
    } ${scopeText}.`;

    return {
      mobilePrimary: noResults,
      mobileSecondary: "",
      desktop: noResults,
    };
  }

  if (!SHOW_COMPANIES) {
    const countText = formatSelectedTypeCountFi(
      total,
      selectedArtistTypes,
      SHOW_COMPANIES
    );

    return {
      mobilePrimary: locationValue
        ? `${countText} · ${locationValue}${dateSuffix}`
        : `${countText}${dateSuffix}`,
      mobileSecondary: `${mobilePriceInfo} · Saatavuus profiilista tai viestillä`,
      desktop: `Löytyi ${countText} ${foundScopeText}. ${priceInfo} ${availabilitySentence} Tarkan hinnan näet ennen tarjouspyynnön lähettämistä.`,
    };
  }

  const c = countByType(list);
  const performers =
    (c?.bilebandi || 0) +
    (c?.trubaduuri || 0) +
    (c?.dj || 0) +
    (c?.sooloartisti || 0) +
    (c?.duo || 0) +
    (c?.other || 0);
  const companies = c?.company || 0;

  const pieces = [];
  if (performers) pieces.push(`${performers} esiintyjää`);
  if (companies) pieces.push(`${companies} yritystä`);

  const totalText = `Löytyi yhteensä ${formatCountFi(
    total,
    "toimija"
  )} ${foundScopeText}`;
  const breakdownText = pieces.length
    ? ` (${pieces.join(" ja ")})`
    : "";

  return {
    mobilePrimary: locationValue
      ? `${formatCountFi(total, "toimija")} · ${locationValue}${dateSuffix}`
      : `${formatCountFi(total, "toimija")}${dateSuffix}`,
    mobileSecondary: `${mobilePriceInfo} · Saatavuus profiilista tai viestillä`,
    desktop: `${totalText}${breakdownText}. ${priceInfo} ${availabilitySentence} Tarkan hinnan näet ennen tarjouspyynnön lähettämistä.`,
  };
}, [
  displayedBands,
  debouncedRegions,
  debouncedLocation,
  selectedDate,
  selectedArtistTypes,
  aiSearchActive,
  aiDateRange,
]);

  const suggestedAlternativeTypes = useMemo(() => {
    const currentType = selectedArtistTypes?.[0] || "";
    if (!currentType) return [];

    const fallbacks = TYPE_FALLBACKS[currentType] || [];
    if (!fallbacks.length) return [];

    return fallbacks.filter((type) => type !== currentType && artistTypesUI.includes(type));
  }, [selectedArtistTypes, artistTypesUI]);

  useEffect(() => {
    setNextAvailableDays([]);
    setNextAvailableSearched(false);
  }, [
    selectedDate,
    selectedRegions,
    selectedArtistTypes,
    selectedGenres,
    selectedServices,
      selectedPrograms,
    selectedLanguages,
    selectedPhotoFacilities,
    selectedPriceBucket,
    priceMin,
    priceMax,
    priceSort,
    debouncedSearchTerm,
    debouncedLocation,
  ]);

  // ---------------------------------------------------------
  // ⚡ Desktop chip -käsittelijät (tyyppi)
  // ---------------------------------------------------------

  const toggleQuickArtistType = useCallback((typeLabel) => {
    const current = selectedArtistTypes?.[0] ?? "";
    const next = (current === typeLabel) ? "" : typeLabel;
    setSelectedArtistTypes(next ? [next] : []);
    // pidetään nykyinen logiikka: kun tyyppi vaihtuu, nollaa tagit
    resetDetailFilters();
    scrollToResults();
    trackArtistTypeSelected(next || "Kaikki");
  }, [selectedArtistTypes, scrollToResults, resetDetailFilters]);

  const handleAlternativeTypeSelect = useCallback((nextType) => {
    if (!nextType) return;

    setSelectedArtistTypes([nextType]);
    resetDetailFilters();
    setNextAvailableDays([]);
    setNextAvailableSearched(false);
    scrollToResults();
    trackArtistTypeSelected(nextType);
  }, [resetDetailFilters, scrollToResults]);

  // ✅ Lisäsuodattimien sisältö yhtenä JSX:nä (renderöidään:
  // - desktopissa hero-laatikossa
  // - mobiilissa tuloslaatikossa “Löytyi …” -tekstin yhteydessä)
  const moreFiltersGrid = (
    <div className="grid grid-cols-1 xxxsm:grid-cols-1 xxsm:grid-cols-1 s24:grid-cols-2 xsm:grid-cols-2 sm:grid-cols-3 md:grid-cols-3 mdd:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 1xl:grid-cols-3 2xl:grid-cols-3 gap-3">

      {/* 🎵 Musiikkityyli EKAKSI lisäsuodattimiin */}
      {showGenreFilter && (
        <label
          className={`text-left w-full ${
            tagsDisabled ? "opacity-40 pointer-events-none select-none" : ""
          }`}
        >
          <span className={labelBase}>Musiikkityyli</span>
          <select
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (v && !selectedGenres.includes(v)) {
                setSelectedGenres([...selectedGenres, v]);
                scrollToResults();
              }
            }}
            disabled={tagsDisabled}
            className={`${inputBase} h-9 px-2 text-sm`}
          >
            <option value="">
              {selectedGenres.length ? `${selectedGenres.length} valittu` : "Kaikki"}
            </option>
            {genresUI.map((g) => (
              <option key={g} value={g} disabled={selectedGenres.includes(g)}>
                {g}
              </option>
            ))}
          </select>

          {QUICK_GENRES.filter((g) => genresUI.includes(g)).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {QUICK_GENRES.filter((g) => genresUI.includes(g)).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => {
                    if (!selectedGenres.includes(g)) {
                      setSelectedGenres([...selectedGenres, g]);
                      scrollToResults();
                    }
                  }}
                  className="text-[10px] px-2 mt-1 py-[2px] rounded-full border border-white/10 bg-white/50 hover:bg-white/80 dark:bg-electric/30 dark:text-white/90 text-black/90 dark:hover:bg-electric/80"
                >
                  {g}
                </button>
              ))}
            </div>
          )}

          {selectedGenres.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {selectedGenres.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() =>
                    setSelectedGenres(selectedGenres.filter((x) => x !== g))
                  }
                  className="bg-electric text-black dark:bg-primary border border-white/10 rounded-full px-2 mt-1 py-[2px] text-[10px] font-semibold dark:hover:bg-teal-100 hover:bg-pink/80 dark:text-black"
                >
                  {g} ✕
                </button>
              ))}
            </div>
          )}
        </label>
      )}

      {/* 📸 Studio (vain valokuvaajille) */}
      {showPhotographerFilters && (
        <label
          className={`text-left w-full ${
            tagsDisabled ? "opacity-40 pointer-events-none select-none" : ""
          }`}
        >
          <span className={labelBase}>Puitteet</span>
          <select
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (v && !selectedPhotoFacilities.includes(v)) {
                setSelectedPhotoFacilities([...selectedPhotoFacilities, v]);
                scrollToResults();
              }
            }}
            disabled={tagsDisabled}
            className={`${inputBase} h-9 px-2 text-sm`}
          >
            <option value="">
              {selectedPhotoFacilities.length
                ? `${selectedPhotoFacilities.length} valittu`
                : "Kaikki"}
            </option>
            <option
              value="Oma studio"
              disabled={selectedPhotoFacilities.includes("Oma studio")}
            >
              Oma studio
            </option>
          </select>

          {selectedPhotoFacilities.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {selectedPhotoFacilities.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() =>
                    setSelectedPhotoFacilities(selectedPhotoFacilities.filter((x) => x !== f))
                  }
                  className="bg-electric text-black dark:bg-primary border border-white/10 rounded-full px-2 mt-1 py-[2px] text-[10px] font-semibold dark:hover:bg-teal-100 hover:bg-pink/80 dark:text-black"
                >
                  {f} ✕
                </button>
              ))}
            </div>
          )}
        </label>
      )}

      {/* ✨ Ohjelmat (ei-musiikkityypeille) */}
      {showEventProgramFilters && (
        <label
          className={`text-left w-full ${
            tagsDisabled ? "opacity-40 pointer-events-none select-none" : ""
          }`}
        >
          <span className={labelBase}>
          {isCircusTypeSelected ? "Esitysmuodot" : "Ohjelmat"}
        </span>
          <select
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (v && !selectedPrograms.includes(v)) {
                setSelectedPrograms([...selectedPrograms, v]);
                scrollToResults();
              }
            }}
            disabled={tagsDisabled || programsUI.length === 0}
            className={`${inputBase} h-9 px-2 text-sm`}
          >
            <option value="">
              {selectedPrograms.length
                ? `${selectedPrograms.length} valittu`
                : programsUI.length
                ? "Kaikki"
                : "Ei vaihtoehtoja"}
            </option>
            {programsUI.map((p) => (
              <option
                key={p.value}
                value={p.value}
                disabled={selectedPrograms.includes(p.value)}
              >
                {p.label}
              </option>
            ))}
          </select>

          {selectedPrograms.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {selectedPrograms.map((id) => {
                const label =
                  programsUI.find((x) => x.value === id)?.label ||
                  prettyFromSlug(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() =>
                      setSelectedPrograms(selectedPrograms.filter((x) => x !== id))
                    }
                    className="bg-electric text-black dark:bg-primary border border-white/10 rounded-full px-2 mt-1 py-[2px] text-[10px] font-semibold dark:hover:bg-teal-100 hover:bg-pink/80 dark:text-black"
                  >
                    {label} ✕
                  </button>
                );
              })}
            </div>
          )}
        </label>
      )}

      {/* 🌍 Kielet (ei-musiikkityypeille) */}
      {showEventProgramFilters && (
        <label
          className={`text-left w-full ${
            tagsDisabled ? "opacity-40 pointer-events-none select-none" : ""
          }`}
        >
          <span className={labelBase}>Kielet</span>
          <select
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (v && !selectedLanguages.includes(v)) {
                setSelectedLanguages([...selectedLanguages, v]);
                scrollToResults();
              }
            }}
            disabled={tagsDisabled || availableLanguages.length === 0}
            className={`${inputBase} h-9 px-2 text-sm`}
          >
            <option value="">
              {selectedLanguages.length
                ? `${selectedLanguages.length} valittu`
                : (availableLanguages.length ? "Kaikki" : "Ei vaihtoehtoja")}
            </option>
            {availableLanguages.map((l) => (
              <option key={l} value={l} disabled={selectedLanguages.includes(l)}>
                {l}
              </option>
            ))}
          </select>

          {selectedLanguages.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {selectedLanguages.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setSelectedLanguages(selectedLanguages.filter((x) => x !== l))}
                  className="bg-electric text-black dark:bg-primary border border-white/10 rounded-full px-2 mt-1 py-[2px] text-[10px] font-semibold dark:hover:bg-teal-100 hover:bg-pink/80 dark:text-black"
                >
                  {l} ✕
                </button>
              ))}
            </div>
          )}
        </label>
      )}

      {/* Tapahtumapaikkakunta */}
      <label className="text-left w-full">
        <span className={labelBase}>Tapahtumapaikkakunta</span>
        <input
          type="text"
          placeholder="Esim. Vantaa"
          value={locationFilter}
          onChange={(e) => {
            setLocationFilter(e.target.value);

          }}
          onBlur={scrollToResults}
          onKeyDown={onCommitScrollKeyDown}
          className={`${inputBase} h-9 px-2 text-sm`}
        />
      </label>

      {/* Etsi nimellä */}
      <label className="text-left w-full">
        <span className={labelBase}>Etsi nimellä</span>
        <input
          type="text"
          placeholder="Etsi…"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);

          }}
          onBlur={scrollToResults}
          onKeyDown={onCommitScrollKeyDown}
          className={`${inputBase} h-9 px-2 text-sm`}
        />
      </label>
    </div>
  );

  // Hero-filtterit pidetään heti näkyvissä:
  // AOS piilottaa [data-aos] -elementit aluksi (opacity:0), mikä aiheutti joillain (mm. Mac/Safari)
  // fiiliksen että filtteri "ei ilmesty heti". Siksi AOS-animaatio poistetaan tästä kriittisestä osasta.
  const filtersSection = (
    <section className="space-y-1">
      {/* PIKA-HAKU (mobiilissa aina näkyvissä: Milloin + Tyyppi + Missä) */}
      <div className="grid grid-cols-1 xxxsm:grid-cols-1 xxsm:grid-cols-1 s24:grid-cols-2 xsm:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 mdd:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 1xl:grid-cols-4 2xl:grid-cols-4 gap-3">

         {/* 💶 Budjetti (bucket + min/max) */}
          <label className="text-left w-full hidden md:block">
            <div>
              <span className={labelBase}>Budjetti</span>
              <select
                value={selectedPriceBucket}
                onChange={(e) => {
                  // ✅ Poissulkeva UX:
                  // - Kun bucket valitaan, Min/Max tyhjennetään ja disabloidaan
                  // - Kun Min/Max syötetään, bucket nollataan (alla inputeissa)
                  const next = e.target.value;
                  setSelectedPriceBucket(next);
                  if (next) {
                    setPriceMin("");
                    setPriceMax("");
                  }
                }}
                className={`${inputBase} h-9 px-2 text-sm`}
              >
                {PRICE_BUCKET_OPTIONS.map((o) => (
                  <option key={o.value || "all"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              {selectedPriceBucket && (
                <div className="mt-1 text-[10px] text-black/60 dark:text-white/60">
                  Min/Max ei ole käytössä, kun budjettihaitari on valittu. Valitse “Kaikki” käyttääksesi Min/Max-rajausta.
                </div>
              )}

              <div className="mt-2 grid grid-cols-2 gap-2">
                <div className="text-left">
                  <span className={labelSmall}>Min €</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="0"
                    value={priceMin}
                    onChange={(e) => {
                      // ✅ jos käyttäjä alkaa syöttää tarkkaa minimiä, nollaa bucket
                      if (selectedPriceBucket) setSelectedPriceBucket("");
                      setPriceMin(sanitizeNonNegativeInt(e.target.value));
                    }}
                    onKeyDown={onCommitScrollKeyDown}
                    disabled={!!selectedPriceBucket}
                    className={`${inputBase} h-9 px-2 text-sm ${selectedPriceBucket ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                </div>

                <div className="text-left">
                  <span className={labelSmall}>Max €</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="Max €"
                    value={priceMax}
                    onChange={(e) => {
                      // ✅ jos käyttäjä alkaa syöttää tarkkaa maksimiä, nollaa bucket
                      if (selectedPriceBucket) setSelectedPriceBucket("");
                      setPriceMax(sanitizeNonNegativeInt(e.target.value));
                    }}
                    onKeyDown={onCommitScrollKeyDown}
                    disabled={!!selectedPriceBucket}
                    className={`${inputBase} h-9 px-2 text-sm ${
                      selectedPriceBucket ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  />
                </div>

              </div>
            </div>
          </label>

        {/* Milloin */}
        <label className="text-left w-full">
          <span className={labelBase}>Päivämäärä</span>
          <input
            type="date"
            value={selectedDate}
             onChange={(e) => {
              const date = e.target.value;
              pendingDateUrlSyncRef.current = date;
              setSelectedDate(date);
              setSelectedDateSource("user");
              setAiDateRange(null);
              scrollToResults();
              trackDateSelected(date);
            }}

            min={todayLocalISO()}
            className={`${inputBase} h-9 px-2 text-sm date--dark`}
          />

        </label>

        {/* Tyyppi (mobiilissa 2. sarake) */}
        <label className="text-left w-full s24:col-span-1 xsm:col-span-1 sm:col-span-1 md:col-span-1">
          <span className={labelBase}>Tekijätyyppi</span>
          <select
            value={selectedArtistTypes[0] ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedArtistTypes(v ? [v] : []);
                resetDetailFilters();
                trackArtistTypeSelected(v || "Kaikki");
              }}
            className={`${inputBase} h-9 px-2 text-sm`}
          >
            <option value="">Kaikki</option>
            {artistTypeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
            {/* ⚡ Esiintyjätyyppi pikachipit (DESKTOP) */}
          {quickArtistTypeChips.length > 0 && (
            <div className="hidden md:flex flex-wrap gap-1">
              {quickArtistTypeChips.map((t) => {
                const active = (selectedArtistTypes?.[0] ?? "") === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleQuickArtistType(t)}
                    className={[
                      "text-[10px] px-2 mt-1 py-[2px] rounded-full border border-white/10",
                      active
                        ? "bg-electric text-black dark:bg-primary"
                        : "bg-white/50 hover:bg-white/80 dark:bg-electric/30 dark:text-white/90 text-black/90 dark:hover:bg-electric/80",
                    ].join(" ")}
                    title={active ? `Poista tyyppi: ${t}` : `Valitse tyyppi: ${t}`}
                    aria-pressed={active}
                  >
                      <span className="inline-flex items-center gap-1">
                      <span>{t}</span>
                      {active && <span aria-hidden>✕</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </label>

        {/* Missä (alue) — mobiilissa täysleveä */}
        <label className="text-left w-full s24:col-span-2 xsm:col-span-2 sm:col-span-1 md:col-span-1">
          <span className={labelBase}>Alue</span>
          <select
            value={selectedRegions[0] ?? ""}
            onChange={(e) => {
              const v = e.target.value;

              if (!v) {
                setSelectedRegions([]);
                scrollToResults();
                return;
              }

              setSelectedRegions((prev) => {
                const isSameSingle = prev.length === 1 && prev[0] === v;
                const next = isSameSingle ? [] : [v];

                if (!isSameSingle) {
                  trackRegionSelected(v);
                }

                return next;
              });

              scrollToResults();
            }}
            className={`${inputBase} h-9 px-2 text-sm`}
          >
            <option value="">Kaikki</option>
            {regionsUI.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          {/* pikavalinnat */}
          {QUICK_REGIONS.filter((r) => regionsUI.includes(r)).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {QUICK_REGIONS.filter((r) => regionsUI.includes(r)).map((r) => {
                const active = selectedRegions[0] === r;

                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setSelectedRegions((prev) =>
                        prev.length === 1 && prev[0] === r ? [] : [r]
                      );
                      if (!active) {
                        trackRegionSelected(r);
                      }
                      scrollToResults();
                    }}
                    className={[
                      "text-[10px] px-2 mt-1 py-[2px] rounded-full border border-white/10",
                      active
                        ? "bg-electric text-black dark:bg-primary"
                        : "bg-white/50 hover:bg-white/80 dark:bg-electric/30 dark:text-white/90 text-black/90 dark:hover:bg-electric/80",
                    ].join(" ")}
                    title={active ? `Poista alue ${r}` : `Valitse alue ${r}`}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          )}
        </label>

</div>

    {/* ✨ Älykäs haku desktopissa */}
    <div className="hidden md:block w-full mt-4">

      <div className="mb-2 px-1 text-left">
        <div
          className="
            font-customFont1
            text-[13px]
            font-semibold
            uppercase
            tracking-[0.06em]

            text-black/95
            dark:text-white/90
          "
        >
          Älykäs haku
        </div>

        <div
          className="
            mt-0.5
            font-customFont1
            text-[11px]
            font-medium

            text-black/60
            dark:text-white/50
          "
        >
          Kerro omin sanoin mitä etsit
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAiSearch();
        }}
        className="
          flex
          h-14
          w-full
          items-center
          gap-2

          rounded-xl

          border
          border-black/25

          bg-[#15121b]

          px-2

          shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_8px_24px_rgba(0,0,0,0.14)]

          dark:border-white/10
          dark:bg-black/25
        "
      >
        <input
          type="text"
          value={aiQuery}
          onChange={(e) => setAiQuery(e.target.value)}
          placeholder="Esim. esiintyjä tai palvelu yritysjuhliin Helsingissä, budjetti noin 1200 €"
          maxLength={500}
          className="
            min-w-0
            flex-1
            h-full

            bg-transparent

            px-3

            font-customFont1
            text-[14px]

            text-white

            outline-none

            placeholder:text-white/55
          "
        />

        {aiQuery && !aiSearchLoading && (
          <button
            type="button"
            onClick={() => {
              setAiQuery("");
              setAiSearchResults([]);
              setAiSearchActive(false);
              setAiDateRange(null);
              if (!selectedDate) {
                setSelectedDate(todayLocalISO());
                setSelectedDateSource("default");
              }
              setAiSearchError("");
            }}
            className="
              inline-flex
              h-10
              w-10
              shrink-0
              items-center
              justify-center

              rounded-xl

              text-white/45

              transition

              hover:bg-white/[0.07]
              hover:text-white/80
            "
            aria-label="Tyhjennä älykäs haku"
          >
            <X size={16} />
          </button>
        )}

        <button
          type="submit"
          disabled={
            aiSearchLoading ||
            aiQuery.trim().length < 3
          }
          className="
            h-10
            shrink-0

            rounded-xl

            bg-white

            px-6

            font-customFont1
            text-[12px]
            font-semibold

            text-[#111]

            shadow-sm

            transition

            hover:bg-white/90

            disabled:cursor-not-allowed
            disabled:opacity-40
          "
        >
          {aiSearchLoading ? "Haetaan…" : "Etsi"}
        </button>
      </form>

      {aiSearchActive && !aiSearchLoading && (
        <div className="mt-2 flex items-center justify-between px-1">
          <span
            className="
              font-customFont1
              text-[11px]
              font-medium

              text-cyan-800/80
              dark:text-cyan-100/60
            "
          >
            {displayedBands.length > 0
              ? `${displayedBands.length} suositusta löytyi${aiDateRangeLabel ? ` · ${aiDateRangeLabel}` : ""}`
              : `Ei sopivia suosituksia${aiDateRangeLabel ? ` · ${aiDateRangeLabel}` : ""}`}
          </span>

          <button
            type="button"
            onClick={() => {
              setAiQuery("");
              setAiSearchResults([]);
              setAiSearchActive(false);
              setAiDateRange(null);
              if (!selectedDate) {
                setSelectedDate(todayLocalISO());
                setSelectedDateSource("default");
              }
              setAiSearchError("");
            }}
            className="
              font-customFont1
              text-[11px]
              font-semibold

              text-black/55
              hover:text-black/80

              dark:text-white/40
              dark:hover:text-white/70
            "
          >
            Poista haku
          </button>
        </div>
      )}

      {aiSearchError && (
        <div
          className="
            mt-2
            px-1

            text-left

            font-customFont1
            text-[11px]
            font-medium

            text-red-700/90
            dark:text-red-300/80
          "
        >
          {aiSearchError}
        </div>
      )}
    </div>

    {/* Toggle: Lisäsuodattimet */}
    <div className="hidden md:flex justify-center w-full py-2 md:py-1 lg:py-2">
      <button
        type="button"
        onClick={() => setShowMoreFilters((v) => !v)}
        aria-expanded={showMoreFilters}
        className="
          group

          inline-flex
          items-center
          gap-2

          rounded-full

          border
          border-black/[0.10]

          bg-white/45

          px-4
          py-2

          font-customFont1
          text-[11px]
          font-semibold

          text-black/70

          shadow-[0_8px_24px_rgba(25,20,40,0.10),inset_0_1px_0_rgba(255,255,255,0.75)]

          backdrop-blur-xl

          transition-all
          duration-200

          hover:border-black/15
          hover:bg-white/60
          hover:text-black/90
          hover:shadow-[0_10px_28px_rgba(25,20,40,0.14),inset_0_1px_0_rgba(255,255,255,0.85)]

          active:scale-[0.98]

          dark:border-white/[0.10]
          dark:bg-white/[0.055]
          dark:text-white/70
          dark:shadow-[0_8px_24px_rgba(0,0,0,0.20),inset_0_1px_0_rgba(255,255,255,0.05)]

          dark:hover:border-white/[0.16]
          dark:hover:bg-white/[0.09]
          dark:hover:text-white
        "
      >
        <span
          className="
            inline-flex
            h-6
            w-6
            items-center
            justify-center

            rounded-full

            border
            border-black/[0.08]

            bg-black/[0.045]

            text-black/55

            transition

            group-hover:bg-black/[0.07]
            group-hover:text-black/80

            dark:border-white/[0.10]
            dark:bg-white/[0.06]
            dark:text-white/55

            dark:group-hover:bg-white/[0.10]
            dark:group-hover:text-white/85
          "
        >
          {showMoreFilters ? (
            <MinusCircle size={14} className="shrink-0" />
          ) : (
            <PlusCircle size={14} className="shrink-0" />
          )}
        </span>

        <span className="whitespace-nowrap">
          {showMoreFilters ? "Sulje suodattimet" : "Lisäsuodattimet"}
        </span>
      </button>
    </div>

      {/* LISÄSUODATTIMET (2. rivi) */}
      {showMoreFilters && (
        <div className="hidden md:block">
          {moreFiltersGrid}
        </div>

      )}
    </section>
  );

  const resultsBar = (
    <div className="w-full text-center my-3 md:my-6">

      {/* =====================================================
          DESKTOP: TULOSTEN JÄRJESTYS
      ===================================================== */}
      <div className="hidden md:flex w-full items-center justify-end mb-4">
        <div
          className="
            inline-flex
            items-center
            gap-1

            rounded-xl

            border
            border-black/10

            bg-white/45
            p-1

            shadow-sm
            backdrop-blur-md

            dark:border-white/10
            dark:bg-white/[0.05]
          "
        >
          {[
            { value: "recommended", label: "Suositellut" },
            { value: "asc", label: "Halvin" },
            { value: "desc", label: "Kallein" },
          ].map((option) => {
            const active = priceSort === option.value;

            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  trackEvent("etusivu_price_sort_changed", {
                    sort: option.value,
                    source: "desktop_results",
                  });

                  setPriceSort(option.value);
                }}
                className={[
                  "rounded-lg px-4 py-2",
                  "font-customFont1 text-[11px] font-semibold",
                  "transition-all duration-200",

                  active
                    ? "bg-black text-white shadow-sm dark:bg-white dark:text-[#0b0612]"
                    : "text-black/55 hover:bg-black/[0.05] hover:text-black/85 dark:text-white/45 dark:hover:bg-white/[0.06] dark:hover:text-white/80",
                ].join(" ")}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

  {/* =====================================================
      📱 MOBIILI: ÄLYKÄS HAKU
  ===================================================== */}
  <div className="md:hidden w-full max-w-2xl mx-auto mb-3 px-3">
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleAiSearch();
      }}
      className="
        relative
        overflow-hidden
        rounded-[24px]

        border
        border-black/[0.08]

        bg-[linear-gradient(145deg,rgba(255,255,255,0.94),rgba(245,241,250,0.92),rgba(232,239,248,0.90))]

        p-3

        shadow-[0_18px_50px_rgba(35,25,60,0.16),inset_0_1px_0_rgba(255,255,255,0.90)]

        ring-1
        ring-white/70

        backdrop-blur-xl

        dark:border-white/[0.08]
        dark:bg-[linear-gradient(145deg,rgba(18,11,28,0.96),rgba(7,4,12,0.98))]
        dark:shadow-[0_18px_50px_rgba(0,0,0,0.32)]
        dark:ring-white/[0.03]
      "
    >
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          inset-x-12
          top-0
          h-px

          bg-gradient-to-r
          from-transparent
          via-cyan-600/25
          to-transparent

          dark:via-cyan-300/50
        "
      />

      <div className="mb-2 text-left">
        <div
          className="
            font-customFont1
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.16em]

            text-black/70
            dark:text-white/60
          "
        >
          Älykäs haku
        </div>

        <div
          className="
            mt-0.5
            font-customFont1
            text-[10px]

            text-black/45
            dark:text-white/35
          "
        >
          Kerro omin sanoin mitä etsit
        </div>
      </div>

      <div
        className="
          flex
          items-center
          gap-2

          rounded-2xl

          border
          border-black/20

          bg-[#15121b]

          p-1.5

          shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]

          dark:border-white/[0.08]
          dark:bg-black/25
        "
      >
        <input
          type="text"
          value={aiQuery}
          onChange={(e) => setAiQuery(e.target.value)}
          placeholder="Esim. esiintyjä tai palvelu juhliin, 1200 €"
          maxLength={500}
          className="
            min-w-0
            flex-1

            bg-transparent

            px-2
            py-2

            font-customFont1
            text-[12px]
            text-white

            outline-none

            placeholder:text-white/65
          "
        />

        {aiQuery && !aiSearchLoading && (
          <button
            type="button"
            onClick={() => {
              setAiQuery("");
              setAiSearchResults([]);
              setAiSearchActive(false);
              setAiDateRange(null);
              if (!selectedDate) {
                setSelectedDate(todayLocalISO());
                setSelectedDateSource("default");
              }
              setAiSearchError("");
            }}
            className="
              inline-flex
              h-8 w-8
              shrink-0
              items-center
              justify-center

              rounded-xl

              text-white/40

              transition

              hover:bg-white/[0.06]
              hover:text-white/75
            "
            aria-label="Tyhjennä älykäs haku"
          >
            <X size={14} />
          </button>
        )}

        <button
          type="submit"
          disabled={
            aiSearchLoading ||
            aiQuery.trim().length < 3
          }
          className="
            h-8
            shrink-0

            rounded-xl

            bg-white

            px-3

            font-customFont1
            text-[10px]
            font-semibold
            text-[#0b0612]

            shadow-sm

            transition

            hover:bg-white/90

            disabled:cursor-not-allowed
            disabled:opacity-35
          "
        >
          {aiSearchLoading ? "Haetaan…" : "Etsi sopivat"}
        </button>
      </div>

      {aiSearchActive && !aiSearchLoading && (
        <div
          className="
            mt-2
            flex
            items-center
            justify-between
            gap-2
            text-left
          "
        >
          <span
            className="
              font-customFont1
              text-[9px]

              text-cyan-900/70
              dark:text-cyan-100/55
            "
          >
            {displayedBands.length > 0
              ? `${displayedBands.length} suositusta löytyi${aiDateRangeLabel ? ` · ${aiDateRangeLabel}` : ""}`
              : `Ei sopivia suosituksia${aiDateRangeLabel ? ` · ${aiDateRangeLabel}` : ""}`}
          </span>

          <button
            type="button"
            onClick={() => {
              setAiQuery("");
              setAiSearchResults([]);
              setAiSearchActive(false);
              setAiDateRange(null);
              if (!selectedDate) {
                setSelectedDate(todayLocalISO());
                setSelectedDateSource("default");
              }
              setAiSearchError("");
            }}
            className="
              font-customFont1
              text-[9px]
              font-semibold

              text-black/45

              transition

              hover:text-black/70

              dark:text-white/35
              dark:hover:text-white/70
            "
          >
            Poista haku
          </button>
        </div>
      )}

      {aiSearchError && (
        <div
          className="
            mt-2
            text-left
            font-customFont1
            text-[9px]

            text-red-700/80
            dark:text-red-300/80
          "
        >
          {aiSearchError}
        </div>
      )}
    </form>
  </div>

  {/* =====================================================
      📱 MOBIILI: PREMIUM-SUODATIN
  ===================================================== */}
  <div className="md:hidden relative z-20 w-full max-w-2xl mx-auto mb-4 px-3">
    <div
      className="
        relative
        overflow-hidden

        rounded-[28px]

        border
        border-black/[0.08]

        bg-[linear-gradient(145deg,rgba(255,255,255,0.94),rgba(245,241,250,0.92),rgba(232,239,248,0.90))]

        shadow-[0_24px_70px_rgba(35,25,60,0.16),inset_0_1px_0_rgba(255,255,255,0.90)]

        ring-1
        ring-white/70

        backdrop-blur-2xl

        dark:border-white/[0.08]
        dark:bg-[linear-gradient(145deg,rgba(18,11,28,0.97),rgba(7,4,12,0.98))]
        dark:shadow-[0_24px_70px_rgba(0,0,0,0.45)]
        dark:ring-white/[0.03]
      "
    >
      {/* premium-hohde */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          inset-x-10
          top-0
          h-px

          bg-gradient-to-r
          from-transparent
          via-cyan-600/25
          to-transparent

          dark:via-cyan-300/50
        "
      />

      <div className="p-4">

        {/* =====================================================
            YLÄRIVI
        ===================================================== */}
        <div className="grid grid-cols-[0.9fr_1.25fr] gap-3">

          {/* BUDJETTI */}
          <label className="min-w-0 text-left">
            <span
              className="
                mb-1.5
                block

                font-customFont1
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.16em]

                text-black/65
                dark:text-white/45
              "
            >
              Budjetti
            </span>

            <select
              value={selectedPriceBucket}
              onChange={(e) => {
                const next = e.target.value;
                setSelectedPriceBucket(next);

                if (next) {
                  setPriceMin("");
                  setPriceMax("");
                }
              }}
              className="
                h-10
                w-full

                rounded-xl

                border
                border-black/20

                bg-[#15121b]

                px-3

                font-customFont1
                text-[13px]
                text-white

                outline-none

                transition

                focus:border-cyan-500/40
                focus:ring-1
                focus:ring-cyan-500/20

                dark:border-white/[0.08]
                dark:bg-white/[0.05]
                dark:focus:border-cyan-300/40
                dark:focus:ring-cyan-300/20
              "
            >
              {PRICE_BUCKET_OPTIONS.map((o) => (
                <option
                  key={o.value || "all"}
                  value={o.value}
                  className="bg-[#100a18] text-white"
                >
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {/* JÄRJESTYS */}
          <div className="min-w-0 text-left">
            <span
              className="
                mb-1.5
                block

                font-customFont1
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.16em]

                text-black/65
                dark:text-white/45
              "
            >
              Järjestys
            </span>

            <div
              className="
                grid
                h-10
                grid-cols-3

                rounded-xl

                border
                border-black/10

                bg-black/[0.08]

                p-1

                dark:border-white/[0.08]
                dark:bg-black/25
              "
            >
              {[
                { value: "recommended", label: "Suositellut" },
                { value: "asc", label: "Halvin" },
                { value: "desc", label: "Kallein" },
              ].map((option) => {
                const active = priceSort === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      trackEvent("etusivu_price_sort_changed", {
                        sort: option.value,
                        source: "mobile",
                      });

                      setPriceSort(option.value);
                    }}
                    className={[
                      "min-w-0 rounded-lg px-1",
                      "font-customFont1 text-[9px] font-semibold",
                      "transition-all duration-200",

                      active
                        ? "bg-white text-[#0b0612] shadow-[0_4px_16px_rgba(0,0,0,0.18)]"
                        : "text-black/45 hover:text-black/75 dark:text-white/40 dark:hover:text-white/70",
                    ].join(" ")}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* =====================================================
            LISÄÄ RAJAUKSIA
        ===================================================== */}
        <button
          type="button"
          onClick={() =>
            setShowMoreFilters((v) => !v)
          }
          aria-expanded={showMoreFilters}
          className="
            mt-4

            flex
            w-full
            items-center
            justify-between

            border-t
            border-black/[0.08]

            pt-3

            text-left

            transition

            dark:border-white/[0.07]
          "
        >
          <span>
            <span
              className="
                block

                font-customFont1
                text-[10px]
                font-semibold

                text-black/70
                dark:text-white/65
              "
            >
              Lisää rajauksia
            </span>

            <span
              className="
                mt-0.5
                block

                font-customFont1
                text-[9px]

                text-black/45
                dark:text-white/30
              "
            >
              Hinta, paikkakunta ja nimihaku
            </span>
          </span>

          <span
            className="
              inline-flex
              h-7
              w-7

              items-center
              justify-center

              rounded-full

              border
              border-black/10

              bg-black/[0.04]

              text-black/55

              dark:border-white/10
              dark:bg-white/[0.05]
              dark:text-white/50
            "
          >
            {showMoreFilters ? (
              <MinusCircle size={13} />
            ) : (
              <PlusCircle size={13} />
            )}
          </span>
        </button>

        {/* =====================================================
            AVATTAVA PREMIUM-SISÄPANEELI
        ===================================================== */}
        {showMoreFilters && (
          <div
            className="
              mt-3

              rounded-2xl

              border
              border-black/[0.08]

              bg-black/[0.025]

              p-3

              dark:border-white/[0.07]
              dark:bg-white/[0.025]
            "
          >
            {/* MIN / MAX */}
            <div className="grid grid-cols-2 gap-2">

              <label className="text-left">
                <span
                  className="
                    mb-1
                    block

                    font-customFont1
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[0.15em]

                    text-black/50
                    dark:text-white/35
                  "
                >
                  Min €
                </span>

                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  placeholder="0"
                  value={priceMin}
                  onChange={(e) => {
                    if (selectedPriceBucket) {
                      setSelectedPriceBucket("");
                    }

                    setPriceMin(
                      sanitizeNonNegativeInt(
                        e.target.value
                      )
                    );
                  }}
                  onKeyDown={onCommitScrollKeyDown}
                  className="
                    h-9
                    w-full

                    rounded-xl

                    border
                    border-black/20

                    bg-[#15121b]

                    px-3

                    font-customFont1
                    text-[12px]
                    text-white

                    outline-none

                    placeholder:text-white/30

                    dark:border-white/[0.07]
                    dark:bg-black/20
                  "
                />
              </label>

              <label className="text-left">
                <span
                  className="
                    mb-1
                    block

                    font-customFont1
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[0.15em]

                    text-black/50
                    dark:text-white/35
                  "
                >
                  Max €
                </span>

                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  placeholder="0"
                  value={priceMax}
                  onChange={(e) => {
                    if (selectedPriceBucket) {
                      setSelectedPriceBucket("");
                    }

                    setPriceMax(
                      sanitizeNonNegativeInt(
                        e.target.value
                      )
                    );
                  }}
                  onKeyDown={onCommitScrollKeyDown}
                  className="
                    h-9
                    w-full

                    rounded-xl

                    border
                    border-black/20

                    bg-[#15121b]

                    px-3

                    font-customFont1
                    text-[12px]
                    text-white

                    outline-none

                    placeholder:text-white/30

                    dark:border-white/[0.07]
                    dark:bg-black/20
                  "
                />
              </label>
            </div>

            {/* =====================================================
                MUUT LISÄSUODATTIMET
            ===================================================== */}
            <div
              className="
                mt-3

                [&_label>span]:!mb-1
                [&_label>span]:!text-[9px]
                [&_label>span]:!font-semibold
                [&_label>span]:!uppercase
                [&_label>span]:!tracking-[0.14em]
                [&_label>span]:!text-black/50

                dark:[&_label>span]:!text-white/35

                [&_input]:!h-9
                [&_input]:!rounded-xl
                [&_input]:!border-black/20
                [&_input]:!bg-[#15121b]
                [&_input]:!text-[12px]
                [&_input]:!text-white
                [&_input]:placeholder:!text-white/30

                dark:[&_input]:!border-white/[0.07]
                dark:[&_input]:!bg-black/20

                [&_select]:!h-9
                [&_select]:!rounded-xl
                [&_select]:!border-black/20
                [&_select]:!bg-[#15121b]
                [&_select]:!text-[12px]
                [&_select]:!text-white

                dark:[&_select]:!border-white/[0.07]
                dark:[&_select]:!bg-black/20
              "
            >
              {moreFiltersGrid}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>

     <AnimatePresence mode="wait">
      <motion.p
        key={`${resultsLabels.mobilePrimary}|${resultsLabels.mobileSecondary}|${resultsLabels.desktop}`}
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -6, scale: 0.98 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="mt-2 text-black/70 font-customFont2 dark:text-white/80 text-shadow-md"
      >
        <span className="block md:hidden">
          <span className="block text-lg font-semibold uppercase leading-snug">
            {resultsLabels.mobilePrimary}
          </span>
          {resultsLabels.mobileSecondary && (
            <span className="block mt-2 text-sm font-normal normal-case leading-relaxed">
              {resultsLabels.mobileSecondary}
            </span>
          )}
        </span>

        <span className="hidden md:block text-2xl sm:text-lg uppercase">
          {resultsLabels.desktop}
        </span>
      </motion.p>
    </AnimatePresence>

      {activeFiltersCount > 0 && (
        <NeonButton
         onClick={() => {
            trackEvent("etusivu_filters_reset", {
              filters_before: activeFiltersCount
            });
            clearFilters();
          }}

          variant="chip"
          size="md"
          className="px-4 py-2 mt-4"
        >
          <span className="flex items-center gap-2 text-white dark:text-white font-bold">
            <span aria-hidden>X</span>
            <span>Nollaa haku</span>
            <span className="ml-2 inline-flex items-center justify-center font-semibold px-2 py-0.5 rounded-full bg-black/20">
              {activeFiltersCount}
            </span>
          </span>
        </NeonButton>
      )}
    </div>
  );

    const cardsSection = (
      <div>
      {displayedBands.length === 0 ? (
        <div className="text-center">

          <div className="mt-4 flex flex-col items-center gap-3">
            <NeonButton
              type="button"
              variant="chip"
              size="md"
              onClick={handleFindNextAvailableDays}
              disabled={nextAvailableLoading}
            >
              {nextAvailableLoading ? "Haetaan seuraavia vapaita…" : "Näytä seuraavat vapaat päivät"}
            </NeonButton>

            {nextAvailableDays.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2">
                {nextAvailableDays.map((item) => (
                  <button
                    key={item.date}
                    type="button"
                    onClick={() => {
                      pendingDateUrlSyncRef.current = item.date;
                      setSelectedDate(item.date);
                      setSelectedDateSource("user");
                      setAiDateRange(null);
                      scrollToResults();
                    }}
                    className="rounded-full border border-white/10 bg-white/60 px-3 py-2 text-sm text-black hover:bg-white/80 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
                  >
                    {formatDateFi(item.date)} · {formatSelectedTypeCountFi(item.count, selectedArtistTypes, SHOW_COMPANIES)}
                  </button>
                ))}
              </div>
            )}

            {nextAvailableSearched && !nextAvailableLoading && nextAvailableDays.length === 0 && (
              <div className="mt-2 flex flex-col items-center gap-3">
                <p className="text-sm font-semibold text-black/60 dark:text-white/60">
                  Seuraaville päiville ei löytynyt osumia samoilla hakuehdoilla.
                </p>

                {suggestedAlternativeTypes.length > 0 && (
                  <>
                    <p className="text-sm text-black/70 dark:text-white/70">
                      Voit katsoa myös muita tekijätyyppejä samalla alueella.
                    </p>

                    <div className="flex flex-wrap justify-center gap-2">
                      {suggestedAlternativeTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleAlternativeTypeSelect(type)}
                          className="rounded-full border border-white/10 bg-white/60 px-3 py-2 text-sm text-black hover:bg-white/80 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
                        >
                          Näytä {type.toLowerCase()}t
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
          <>
            <motion.div
              className="grid grid-cols-2 xxxsm:grid-cols-1 xxsm:grid-cols-1 s24:grid-cols-1 xsm:grid-cols-1 sm:grid-cols-2 mdd:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 w-full mt-5"
              variants={listVariants}
              initial="hidden"
              animate="show"
              layout
            >
              <AnimatePresence initial={false}>
                {displayedBands.slice(0, visibleCount).map((item) => {
                  const key = `${item.__kind}-${item.id}`;

                  const openProfile = (targetPath) => {
                   trackProfileOpened({
                      id: item.id,
                      name: item.name,
                      slug: item.slug,
                      date: selectedDate
                    });

                    // AI-haun month/range-intentti ei mahdu profiilin URL-parametreihin.
                    // Ota siksi yhden paluun mittainen snapshot ennen profiiliin siirtymistä.
                    // Snapshot kulutetaan etusivulle palattaessa eikä jää elämään session ajaksi.
                    try {
                      if (aiSearchActive) {
                        sessionStorage.setItem(
                          HOME_AI_RETURN_KEY,
                          JSON.stringify({
                            active: true,
                            query: aiQuery,
                            results: aiSearchResults,
                            dateRange: aiDateRange,
                            selectedDate,
                            selectedDateSource,
                          })
                        );
                      } else {
                        sessionStorage.removeItem(HOME_AI_RETURN_KEY);
                      }
                    } catch {}

                    // Vie profiiliin vain oikeasti valittu tarkka päivä.
                    // Kuukausi-/range-haussa selectedDate on tarkoituksella tyhjä,
                    // joten siinä tilanteessa EI keksitä tämän päivän päivämäärää.
                    try {
                      if (
                        selectedDate &&
                        /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
                      ) {
                        localStorage.setItem(DATE_KEY, selectedDate);
                      } else {
                        localStorage.removeItem(DATE_KEY);
                      }
                    } catch {}

                    window.scrollTo({ top: 0, behavior: "smooth" });

                      // Kortin perusklikkaus = profiilin yläosa.
                      // Vain oikeasti valittu tarkka päivä säilytetään localStoragessa.
                      // Kuukausi-/range-hakua ei muuteta tekaistuksi yksittäiseksi päiväksi.
                      // Päivää EI viedä profiili-URLiin, koska profiilisivu voi tulkita
                      // ?date=... arvon saatavuus-/kalenteri-intentioksi ja scrollata alas.
                      window.scrollTo({ top: 0, left: 0, behavior: "auto" });

                    if (typeof targetPath === "string" && targetPath.trim()) {
                      const basePrefix =
                        SHOW_COMPANIES && item.__kind === "company"
                          ? "/palvelu/"
                          : "/profiili/";

                        const normalizedTarget = targetPath.startsWith("/")
                          ? targetPath
                          : `${basePrefix}${targetPath}`;

                        // Säilytä vain turvalliset hakuehdot profiiliin siirryttäessä.
                        // Ei date/book/scrollTo/hashia kortin perusklikissä.
                        const [pathAndSearch] = normalizedTarget.split("#");
                        const [pathOnly, existingSearch = ""] = pathAndSearch.split("?");

                        const params = new URLSearchParams(existingSearch || rrLocation.search || "");

                        params.delete("date");
                        params.delete("book");
                        params.delete("scrollTo");

                        const finalSearch = params.toString();
                        const finalHash = "";

                        navigate(`${pathOnly}${finalSearch ? `?${finalSearch}` : ""}${finalHash}`, {
                          state: {
                            profileOpenMode: "top",
                            suppressAvailabilityScroll: true,
                          },
                        });

                        requestAnimationFrame(() => {
                          setTimeout(() => {
                            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
                          }, 120);
                        });
                      return;
                    }

                    const params = new URLSearchParams(rrLocation.search || "");
                      params.delete("date");
                      params.delete("book");
                      params.delete("scrollTo");

                    if (SHOW_COMPANIES && item.__kind === "company") {
                      navigate(`/palvelu/${item.slug}${params.toString() ? `?${params.toString()}` : ""}`, {
                        state: {
                          profileOpenMode: "top",
                          suppressAvailabilityScroll: true,
                        },
                      });
                    } else {
                      navigate(`/profiili/${item.slug}${params.toString() ? `?${params.toString()}` : ""}`, {
                        state: {
                          profileOpenMode: "top",
                          suppressAvailabilityScroll: true,
                        },
                      });
                    }
                  };

                  return (
                    <motion.div key={key} variants={itemVariants} layout exit="exit">
                      {item.__kind === "company" && SHOW_COMPANIES ? (
                        <CompanyCard company={item} onViewProfile={openProfile} />
                      ) : (
                        <ArtistCard band={item} onViewProfile={openProfile} />
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>

            {/* Näytä lisää / vähemmän -napit */}
            <div className="mt-6 flex justify-center gap-3">
              {visibleCount < displayedBands.length && (
                <NeonButton
                  onClick={() => {
                    trackEvent("etusivu_load_more", {
                      visible_before: visibleCount,
                      total: displayedBands.length,
                    });
                    setVisibleCount((c) => Math.min(c + pageSize, displayedBands.length));
                  }}
                  variant="primary"
                  size="md"
                >
                  Näytä lisää ({displayedBands.length - visibleCount})
                </NeonButton>
              )}

              {visibleCount > pageSize && (
                <NeonButton
                  onClick={() => {
                    trackEvent("etusivu_show_less", { visible_before: visibleCount });
                    setVisibleCount(pageSize);
                  }}
                  variant="secondary"
                  size="md"
                >
                  Näytä vähemmän
                </NeonButton>
              )}
            </div>
          </>
        )}
      </div>
    );

const handleApplyFromModal = ({ date, types, genres, services, regions, location: modalLocation, _preview }) => {
  const safeTypes = SHOW_COMPANIES
    ? (types || [])
    : (types || []).filter((t) => String(t).toLowerCase() !== "yritys");

  const safeGenres = genres || [];
  const safeServices = SHOW_COMPANIES ? (services || []) : [];
  const safeRegions = regions || [];
  const safeLocation = typeof modalLocation === "string" ? modalLocation : locationFilter;

  // Live-esikatselu ei saa kirjoittaa etusivun statea eikä URL:ia,
  // muuten query-parametrit alkavat elää modaalin käytön aikana.
  if (_preview) return;

  // Päivitä filtterit aina myös paikalliseen stateen
  if (date) {
    pendingDateUrlSyncRef.current = date;
    setSelectedDate(date);
    setSelectedDateSource("user");
    setAiDateRange(null);
  }
  setSelectedArtistTypes(safeTypes);
  resetDetailFilters();
  setSelectedGenres(safeGenres);
  setSelectedServices(safeServices);
  setSelectedRegions(safeRegions);
  setLocationFilter(safeLocation);

  // Lopullinen URL = sama totuus kuin state
  const params = new URLSearchParams();

  if (date) params.set("date", date);

const firstTypeParam = uiTypeToParam(safeTypes?.[0] || "");
if (firstTypeParam) {
  params.set("type", firstTypeParam);
}
  safeGenres.forEach((g) => params.append("genre", g));
  safeServices.forEach((s) => params.append("service", s));
  if (safeRegions.length) {
    params.set("maakunta", namesToSlugList(safeRegions).join(","));
  }
  if (safeLocation.trim()) {
    params.set("location", safeLocation.trim());
  }

  setShowBooking(false);

  navigate(
    {
      pathname: "/",
      search: params.toString() ? `?${params.toString()}` : "",
      hash: `#${RESULTS_ID}`,
    },
    { state: { scrollTo: RESULTS_ID } }
  );

  // Pakota scroll tuloksiin navigoinnin jälkeen
  setTimeout(() => {
    const el = document.getElementById(RESULTS_ID);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, 80);
};

   // 3) Päivitetty heroTitle (fade vain esiintyjä-versioon)
const heroTitle = SHOW_COMPANIES
  ? "Löydä esiintyjät ja tapahtumapalvelut helposti"
  : "Varaa esiintyjä tai tapahtumapalvelu helposti";

const heroSubtitle = SHOW_COMPANIES
  ? "Vertaa vaihtoehtoja, tarkista saatavuus ja näe hinta ennen tarjouspyyntöä."
  : "Vertaa vaihtoehtoja, tarkista saatavuus ja näe hinta ennen tarjouspyyntöä.";

const heroTrustItems = [
  {
    label: "100 % suomalainen palvelu",
    tone: `
      border-sky-700/20
      bg-white/85
      text-slate-800

      dark:border-sky-300/30
      dark:bg-sky-400/15
      dark:text-sky-50
    `,
  },
  {
    label: "Hinta ennen lähettämistä",
    tone: `
      border-cyan-700/20
      bg-white/85
      text-slate-800

      dark:border-cyan-300/30
      dark:bg-cyan-400/15
      dark:text-cyan-50
    `,
  },
  {
    label: "Saatavuus kalenterista",
    tone: `
      border-emerald-700/20
      bg-white/85
      text-slate-800

      dark:border-emerald-300/30
      dark:bg-emerald-400/15
      dark:text-emerald-50
    `,
  },
  {
    label: "Ei rekisteröitymistä",
    tone: `
      border-violet-700/20
      bg-white/85
      text-slate-800

      dark:border-violet-300/30
      dark:bg-violet-400/15
      dark:text-violet-50
    `,
  },
];

const heroSocialProof = (
  <motion.div
    initial={{
      opacity: 0,
      y: 8,
    }}
    animate={{
      opacity: 1,
      y: 0,
    }}
    transition={{
      duration: 0.45,
      delay: 0.75,
      ease: "easeOut",
    }}
    className="
      mx-auto
      mt-3
      flex
      max-w-3xl
      items-center
      justify-center
      gap-2
      rounded-full
      border border-black/15
    bg-black/55

    dark:border-white/10
    dark:bg-black/25
      px-4 py-2
      text-center
      shadow-lg
      backdrop-blur-md
    "
  >
    <span
      className="
        shrink-0
        text-[11px]
        tracking-[0.12em]
        text-yellow-300
        sm:text-xs
      "
      aria-label="Viisi tähteä"
    >
      ★★★★★
    </span>

    <span
      className="
        font-customFont1
        text-[10px]
        leading-snug
        text-white/90
        sm:text-xs
        md:text-sm
      "
    >
      “Budjettiin sopiva bilebändi löytyi helposti ja varaus sujui nopeasti.”
    </span>

    <span
      className="
        hidden
        shrink-0
        text-[10px]
        text-white/55
        lg:inline
      "
    >
      — Buukkerin asiakas
    </span>
  </motion.div>
);

  return (
    <>
      <Helmet>
        <title>Varaa esiintyjä tai tapahtumapalvelu juhliin ja tapahtumiin | Buukkeri</title>
        <meta
          name="description"
          content="Löydä bilebändi, DJ, trubaduuri, taikuri, valokuvaaja tai sirkus- ja show-esiintyjä juhliin ja tapahtumiin. Tarkista saatavuus ja näe hinta ennen tarjouspyynnön lähettämistä."
        />
      </Helmet>

      <AnimatePresence>
        {HOME_EMBED_TOAST_ENABLED && showEmbedToast && (
          <motion.div
            initial={{ opacity: 0, y: -16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="fixed inset-x-2 top-[72px] z-[120] md:left-1/2 md:right-auto md:w-full md:max-w-md md:-translate-x-1/2 md:top-[80px]"
            layout
            role="status"
            aria-live="polite"
          >
            <div className="rounded-2xl border border-white/15 bg-[#111]/90 px-4 py-3 text-white shadow-2xl backdrop-blur-xl dark:bg-black/80">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-customFont2 text-[11px] sm:text-sm uppercase tracking-wide text-electric dark:text-primary">
                    Uutta palveluntarjoajille
                  </p>
                  <p className="mt-1 font-customFont1 text-[11px] sm:text-sm leading-[1.35] sm:leading-snug text-white/90">
                    Ota Buukkerin varauskalenteri käyttöön omilla sivuillasi alkaen 29 €/kk + alv 25,5 %.
                  </p>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        closeEmbedToast();
                        navigate("/buukkeri-embed");
                      }}
                      className="inline-flex items-center justify-center rounded-full bg-electric px-3 py-1.5 text-xs font-semibold text-black transition hover:scale-[1.02] hover:bg-pink dark:bg-primary dark:hover:bg-teal-100"
                    >
                      Lue lisää
                    </button>

                    <button
                      type="button"
                      onClick={closeEmbedToast}
                     className="hidden sm:inline-flex items-center justify-center rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/85 transition hover:bg-white/10"
                    >
                      Sulje
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeEmbedToast}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
                  aria-label="Sulje ilmoitus"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-end">
                   <span className="hidden sm:inline font-customFont1 text-[10px] sm:text-xs text-white/50">
                    Sulkeutuu automaattisesti
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                  <motion.div
                    initial={{ width: "100%" }}
                    animate={{ width: "0%" }}
                    transition={{ duration: embedToastDurationSec, ease: "linear" }}
                    className="h-full rounded-full bg-electric dark:bg-primary"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
        {/* 🔄 Boot-overlay etusivun lataukseen
          - Taustakuva: vaihda import heroBg → omaan
          - Fade-ajat: fadeInMs / fadeOutMs
          - Logon koko/nopeus: markSize / logoSpinSec
      */}
      {bootLoading && !isMobileHero && (
        <PageOverlayLoading
          label="Ladataan sisältöä…"
          bgImage={heroBg}
          fadeInMs={0}
          fadeOutMs={600}
          logoLight={logoLight}
          logoDark={logoDark}
        />

      )}

{/* HERO */}
<WebDesignBanner
  mp4Src={heroMp4Src}
  poster={heroPoster}
  compact
>
  <div className="w-full text-center">
    {/* =====================================================
        OTSIKKO
    ====================================================== */}
    <motion.div
      initial={{
        opacity: 0,
        y: 12,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        duration: 0.55,
        delay: 0.3,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="
        mx-auto
        mb-5
        max-w-5xl
      "
    >
      <h1
        className="
          font-customFont2
          text-[1.75rem]
          font-semibold
          leading-[1.08]
          text-white
          text-shadow-lg

          sm:text-[2.15rem]
          md:text-[2.7rem]
          lg:text-[3.05rem]
        "
      >
        {heroTitle}
      </h1>

        <p
          className="
            mx-auto
            mt-3
            max-w-3xl
            font-customFont1
            text-sm
            font-semibold
            leading-relaxed
            text-white
            [text-shadow:0_2px_4px_rgba(0,0,0,0.95),0_0_14px_rgba(0,0,0,0.75)]
            sm:text-base
            md:text-lg
          "
        >
          {heroSubtitle}
        </p>
    </motion.div>

    {/* =====================================================
        HAKUBOKSI
    ====================================================== */}
    <motion.div
      initial={{
        opacity: 0,
        y: 18,
        scale: 0.985,
      }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      transition={{
        duration: 0.6,
        delay: 0.42,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="
        relative
        z-30
        mx-auto
        w-full
        max-w-7xl
      "
    >
      {/* Kevyt glow hakukortin ympärille */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          -inset-[1px]
          rounded-[30px]

          bg-gradient-to-r
          from-cyan-500/10
          via-fuchsia-500/10
          to-violet-500/10

          blur-[2px]

          dark:from-cyan-400/20
          dark:via-fuchsia-400/10
          dark:to-violet-400/20
        "
      />

      <div
        className="
          relative
          overflow-visible
          rounded-[30px]

          border
          border-black/[0.08]

          bg-[linear-gradient(135deg,rgba(255,255,255,0.82),rgba(248,244,255,0.78),rgba(238,248,255,0.74))]

          p-5

          shadow-[0_26px_70px_rgba(36,28,60,0.16),inset_0_1px_0_rgba(255,255,255,0.90)]

          ring-1
          ring-white/60

          backdrop-blur-2xl

          dark:border-white/[0.10]
          dark:bg-[linear-gradient(135deg,rgba(22,27,39,0.82),rgba(29,15,48,0.84),rgba(8,10,18,0.88))]
          dark:shadow-[0_28px_80px_rgba(0,0,0,0.30),inset_0_1px_0_rgba(255,255,255,0.08)]
          dark:ring-white/[0.04]

          md:p-5
          lg:p-6
        "
      >
        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            inset-x-16
            top-0
            h-px

            bg-gradient-to-r
            from-transparent
            via-cyan-600/25
            to-transparent

            dark:via-cyan-200/50
          "
        />

        {/* =====================================================
            MOBIILI
            Nykyinen hero pidetään ennallaan.
        ====================================================== */}
        <div className="md:hidden">
          {filtersSection}
        </div>

        {/* =====================================================
            DESKTOP: AI-FIRST HAKU
        ====================================================== */}
        <div className="hidden md:block">
          <div className="mx-auto w-full max-w-5xl">
            <div className="mb-3 text-center">
              <div
                className="
                  inline-flex
                  items-center
                  gap-2

                  rounded-full

                  border
                  border-black/[0.08]

                  bg-white/45

                  px-3
                  py-1.5

                  font-customFont1
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.14em]

                  text-black/60

                  shadow-sm
                  backdrop-blur-xl

                  dark:border-white/[0.09]
                  dark:bg-white/[0.05]
                  dark:text-white/60
                "
              >
                <span
                  aria-hidden="true"
                  className="
                    text-[13px]
                    text-violet-600
                    dark:text-cyan-300
                  "
                >
                  ✦
                </span>

                Buukkerin älykäs haku
              </div>

              <div
                className="
                  mt-2

                  font-customFont2
                  text-[18px]
                  font-semibold
                  leading-tight

                  text-black/85

                  dark:text-white/90

                  lg:text-[20px]
                "
              >
                Kerro mitä etsit – etsimme sopivat vaihtoehdot
              </div>

              <div
                className="
                  mx-auto
                  mt-1
                  max-w-2xl

                  font-customFont1
                  text-[11px]
                  leading-relaxed

                  text-black/50

                  dark:text-white/45
                "
              >
                Voit kirjoittaa esimerkiksi esiintyjätyypin, esitysmuodon, paikan,
                juhlan, musiikkityylin, ajankohdan tai budjetin.
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAiSearch();
              }}
              className="
                group
                relative

                flex
                min-h-[68px]
                w-full
                items-center
                gap-2

                overflow-hidden

                rounded-[22px]

                border
                border-black/20

                bg-[#15121b]

                p-2

                shadow-[0_18px_45px_rgba(25,18,42,0.22),inset_0_1px_0_rgba(255,255,255,0.07)]

                transition-all
                duration-300

                focus-within:border-cyan-500/40
                focus-within:shadow-[0_22px_60px_rgba(25,18,42,0.28),0_0_0_1px_rgba(34,211,238,0.10),inset_0_1px_0_rgba(255,255,255,0.08)]

                dark:border-white/[0.10]
                dark:bg-black/35

                dark:focus-within:border-cyan-300/30
              "
            >
              {/* hienovarainen ylähehku */}
              <div
                aria-hidden="true"
                className="
                  pointer-events-none
                  absolute
                  inset-x-14
                  top-0
                  h-px

                  bg-gradient-to-r
                  from-transparent
                  via-cyan-300/45
                  to-transparent

                  opacity-70
                "
              />

              {/* AI-merkki */}
              <div
                aria-hidden="true"
                className="
                  ml-1

                  hidden
                  h-10
                  w-10
                  shrink-0
                  items-center
                  justify-center

                  rounded-xl

                  border
                  border-white/[0.08]

                  bg-white/[0.055]

                  text-[17px]
                  text-cyan-200

                  shadow-inner

                  lg:flex
                "
              >
                ✦
              </div>

              <input
                type="text"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder="Esim. esiintyjä tai palvelu juhliin Helsingissä 15.11, budjetti noin 1500 €"
                maxLength={500}
                autoComplete="off"
                aria-label="Kerro mitä esiintyjää tai palvelua etsit"
                className="
                  min-w-0
                  flex-1
                  self-stretch

                  bg-transparent

                  px-3

                  font-customFont1
                  text-[14px]
                  font-medium

                  text-white

                  outline-none

                  placeholder:font-normal
                  placeholder:text-white/40

                  lg:text-[15px]
                "
              />

              {aiQuery && !aiSearchLoading && (
                <button
                  type="button"
                  onClick={() => {
                    setAiQuery("");
                    setAiSearchResults([]);
                    setAiSearchActive(false);
                    setAiDateRange(null);
                    if (!selectedDate) {
                      setSelectedDate(todayLocalISO());
                      setSelectedDateSource("default");
                    }
                    setAiSearchError("");
                  }}
                  className="
                    inline-flex
                    h-10
                    w-10
                    shrink-0
                    items-center
                    justify-center

                    rounded-xl

                    text-white/35

                    transition-all
                    duration-200

                    hover:bg-white/[0.07]
                    hover:text-white/75

                    focus:outline-none
                    focus-visible:ring-2
                    focus-visible:ring-white/30
                  "
                  aria-label="Tyhjennä älykäs haku"
                >
                  <X size={17} />
                </button>
              )}

              <button
                type="submit"
                disabled={
                  aiSearchLoading ||
                  aiQuery.trim().length < 3
                }
                className="
                  relative

                  inline-flex
                  h-12
                  shrink-0
                  items-center
                  justify-center
                  gap-2

                  overflow-hidden

                  rounded-[15px]

                  bg-white

                  px-5

                  font-customFont1
                  text-[12px]
                  font-bold

                  text-[#110d18]

                  shadow-[0_8px_24px_rgba(0,0,0,0.24)]

                  transition-all
                  duration-200

                  hover:-translate-y-[1px]
                  hover:bg-white/95
                  hover:shadow-[0_12px_28px_rgba(0,0,0,0.30)]

                  active:translate-y-0

                  disabled:cursor-not-allowed
                  disabled:opacity-35
                  disabled:hover:translate-y-0

                  lg:px-7
                "
              >
                <span
                  aria-hidden="true"
                  className="
                    text-[13px]
                    text-violet-700
                  "
                >
                  ✦
                </span>

                <span>
                  {aiSearchLoading
                    ? "Etsitään…"
                    : "Etsi sopivat"}
                </span>
              </button>
            </form>

            {/* AI-haun palaute */}
            <div className="min-h-[26px]">
              {aiSearchLoading && (
                <div
                  className="
                    mt-2
                    flex
                    items-center
                    justify-center
                    gap-2

                    font-customFont1
                    text-[11px]
                    font-medium

                    text-black/50

                    dark:text-white/45
                  "
                >
                  <span
                    aria-hidden="true"
                    className="
                      inline-block
                      h-1.5
                      w-1.5
                      animate-pulse
                      rounded-full
                      bg-cyan-500
                    "
                  />

                  Haetaan sopivimpia vaihtoehtoja…
                </div>
              )}

              {aiSearchActive && !aiSearchLoading && (
                <div
                  className="
                    mt-2
                    flex
                    items-center
                    justify-between
                    gap-4

                    px-1
                  "
                >
                  <span
                    className="
                      font-customFont1
                      text-[11px]
                      font-semibold

                      text-cyan-800/80

                      dark:text-cyan-100/65
                    "
                  >
                    {displayedBands.length > 0
                      ? `${displayedBands.length} sopivaa vaihtoehtoa löytyi${aiDateRangeLabel ? ` · ${aiDateRangeLabel}` : ""}`
                      : `Haulla ei löytynyt sopivia vaihtoehtoja${aiDateRangeLabel ? ` · ${aiDateRangeLabel}` : ""}`}
                  </span>

                  <button
                    type="button"
                    onClick={() => {
                      setAiQuery("");
                      setAiSearchResults([]);
                      setAiSearchActive(false);
                      setAiDateRange(null);
                      if (!selectedDate) {
                        setSelectedDate(todayLocalISO());
                        setSelectedDateSource("default");
                      }
                      setAiSearchError("");
                    }}
                    className="
                      shrink-0

                      font-customFont1
                      text-[11px]
                      font-semibold

                      text-black/45

                      transition

                      hover:text-black/75

                      dark:text-white/35
                      dark:hover:text-white/65
                    "
                  >
                    Poista haku
                  </button>
                </div>
              )}

              {aiSearchError && (
                <div
                  className="
                    mt-2
                    px-1

                    text-center

                    font-customFont1
                    text-[11px]
                    font-medium

                    text-red-700/90

                    dark:text-red-300/80
                  "
                >
                  {aiSearchError}
                </div>
              )}
            </div>
          </div>

          {/* =====================================================
              MANUAALIHAKU TOISSIJAISENA
          ====================================================== */}
          <div
            className="
              my-2
              flex
              items-center
              gap-4
            "
          >
            <div
              aria-hidden="true"
              className="
                h-px
                flex-1

                bg-gradient-to-r
                from-transparent
                to-black/[0.10]

                dark:to-white/[0.10]
              "
            />

            <span
              className="
                shrink-0

                font-customFont1
                text-[10px]
                font-semibold
                uppercase
                tracking-[0.12em]

                text-black/40

                dark:text-white/35
              "
            >
              Tai tarkenna hakua itse
            </span>

            <div
              aria-hidden="true"
              className="
                h-px
                flex-1

                bg-gradient-to-l
                from-transparent
                to-black/[0.10]

                dark:to-white/[0.10]
              "
            />
          </div>

          {/*
            filtersSection sisältää tällä hetkellä myös vanhan
            desktopin älykkään haun toisena suorana div-lapsena.

            Piilotetaan VAIN se desktopissa.
            Päivämäärä / tekijätyyppi / alue / budjetti sekä
            lisäsuodattimet jäävät normaalisti käyttöön.
          */}
          <div
            className="
              [&>section>div:nth-child(2)]:hidden
            "
          >
            {filtersSection}
          </div>
        </div>
      </div>
    </motion.div>

   {/* =====================================================
          TRUST-PILLIT
      ====================================================== */}
      <div
        className="
          mx-auto
          mt-2

          grid
          w-full
          max-w-[340px]
          grid-cols-2
          gap-1.5

          sm:flex
          sm:max-w-5xl
          sm:flex-wrap
          sm:items-center
          sm:justify-center
          sm:gap-2
          sm:mt-3
        "
      >
        {heroTrustItems.map((item, index) => (
          <motion.span
            key={item.label}
            initial={{
              opacity: 0,
              y: 6,
              scale: 0.96,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            transition={{
              duration: 0.38,
              delay: 0.54 + index * 0.07,
              ease: "easeOut",
            }}
            className={`
              inline-flex
              min-w-0
              items-center
              justify-center
              gap-1

              whitespace-nowrap
              rounded-full
              border

              px-2
              py-1

              font-customFont1
              text-[9px]
              font-semibold
              shadow-md
              backdrop-blur-md

              sm:px-3
              sm:py-1.5
              sm:text-[11px]

              ${item.tone}
            `}
          >
            <span
              className="
                inline-flex
                h-4 w-4
                shrink-0
                items-center
                justify-center
                rounded-full
                bg-white/10
                text-[9px]
              "
              aria-hidden="true"
            >
              ✓
            </span>

            {item.label}
          </motion.span>
        ))}
      </div>

    {/* =====================================================
        SOCIAL PROOF
    ====================================================== */}
    {heroSocialProof}
  </div>
</WebDesignBanner>

      {/* SIVUN SISÄLTÖ */}
      <div className="relative min-h-screen w-full pb-28 sm:pb-10">
        <div className="relative z-10 max-w-[1600px] mx-auto sm:px-4 md:px-4 mdd:px-4 lg:px-4 xl:px-6 2xl:px-8">
          <div className="grid grid-cols-1 gap-4 ">
            <div className="order-1 lg:col-span-3" />
            {/* Kortit laatikossa */}
            <div className="order-3 lg:order-2 lg:col-span-3 scroll-mt-[96px]" id={RESULTS_ID}>
              <div className="rounded-2xl p-0 pt-3 md:p-6 bg-white dark:bg-[#07080b] backdrop-blur-md shadow-lg mt-0 md:mt-10">
                 {resultsBar}

                {/* Sisäiset linkit tärkeimmille hintaländäreille.
                    Mobiilissa kompakti premium-versio, desktopissa laajempi. */}
                <nav
                  aria-label="Suositut haut ja hintasivut"
                  className="mx-auto mb-6 max-w-5xl text-center"
                >
                  <div
                    className="
                      mx-auto
                      max-w-5xl

                      rounded-[24px]

                      border
                      border-black/10

                      bg-white/75

                      px-3
                      py-3

                      shadow-sm

                      backdrop-blur-md

                      dark:border-white/10
                      dark:bg-white/5

                      md:flex
                      md:max-w-fit
                      md:flex-wrap
                      md:items-center
                      md:justify-center
                      md:gap-2
                    "
                  >
                    <span
                      className="
                        mb-2
                        block

                        font-customFont1
                        text-[10px]
                        font-semibold
                        uppercase
                        tracking-[0.12em]

                        text-black/50

                        dark:text-white/50

                        md:mb-0
                        md:text-sm
                        md:tracking-wide
                      "
                    >
                      Katso hintoja:
                    </span>

                    {/* MOBIILI: vain tärkeimmät heti */}
                    <div className="flex flex-wrap justify-center gap-1.5 md:hidden">
                      {LANDING_PRICE_LINKS_PRIMARY.slice(0, 4).map((link) => (
                        <a
                          key={link.href}
                          href={link.href}
                          className="
                            rounded-full

                            border
                            border-black/[0.08]

                            bg-white/70

                            px-2.5
                            py-1.5

                            font-customFont1
                            text-[10px]
                            font-semibold

                            text-black/70

                            shadow-[0_3px_10px_rgba(0,0,0,0.05)]

                            transition

                            active:scale-[0.98]

                            dark:border-white/10
                            dark:bg-white/[0.07]
                            dark:text-white/75
                          "
                        >
                          {link.label}
                        </a>
                      ))}

                      <button
                        type="button"
                        onClick={() => setShowAllLandingLinks((v) => !v)}
                        aria-expanded={showAllLandingLinks}
                        className="
                          inline-flex
                          items-center
                          justify-center
                          gap-1.5

                          rounded-full

                          border
                          border-black/[0.10]

                          bg-black/[0.05]

                          px-2.5
                          py-1.5

                          font-customFont1
                          text-[10px]
                          font-semibold

                          text-black/65

                          transition

                          active:scale-[0.98]

                          dark:border-white/10
                          dark:bg-white/[0.07]
                          dark:text-white/70
                        "
                      >
                        <span
                          className="
                            inline-flex
                            h-4
                            w-4
                            items-center
                            justify-center

                            rounded-full

                            border
                            border-black/10

                            text-[10px]
                            leading-none

                            dark:border-white/15
                          "
                        >
                          {showAllLandingLinks ? "−" : "+"}
                        </span>

                        {showAllLandingLinks
                          ? "Piilota"
                          : "Lisää hintasivuja"}
                      </button>
                    </div>

                    {/* DESKTOP: nykyinen primary-lista */}
                    <div className="hidden md:contents">
                      {LANDING_PRICE_LINKS_PRIMARY.map((link) => (
                        <a
                          key={link.href}
                          href={link.href}
                          className="
                            rounded-full

                            border
                            border-black/10

                            bg-white/90

                            px-3
                            py-1.5

                            font-customFont1
                            text-xs
                            font-semibold

                            text-black/80

                            shadow-sm

                            transition

                            hover:-translate-y-0.5
                            hover:bg-white
                            hover:shadow-md

                            dark:border-white/10
                            dark:bg-white/10
                            dark:text-white/85
                            dark:hover:bg-white/15
                          "
                        >
                          {link.label}
                        </a>
                      ))}

                      <button
                        type="button"
                        onClick={() => setShowAllLandingLinks((v) => !v)}
                        aria-expanded={showAllLandingLinks}
                        className="
                          rounded-full

                          border
                          border-black/10

                          bg-white/50

                          px-3
                          py-1.5

                          font-customFont1
                          text-xs
                          font-semibold

                          text-black/65

                          shadow-sm

                          backdrop-blur-md

                          transition

                          hover:-translate-y-0.5
                          hover:bg-white/75
                          hover:text-black/90
                          hover:shadow-md

                          dark:border-white/10
                          dark:bg-white/[0.07]
                          dark:text-white/70
                          dark:hover:bg-white/10
                          dark:hover:text-white
                        "
                      >
                        {showAllLandingLinks
                          ? "Piilota hintasivut"
                          : "Näytä kaikki hintasivut"}
                      </button>
                    </div>
                  </div>

                  {showAllLandingLinks && (
                    <div
                      className="
                        mx-auto
                        mt-3
                        max-w-4xl

                        rounded-[24px]

                        border
                        border-black/10

                        bg-white/80

                        p-3

                        shadow-[0_14px_36px_rgba(30,20,50,0.08)]

                        backdrop-blur-xl

                        dark:border-white/10
                        dark:bg-black/25

                        md:mt-4
                        md:rounded-3xl
                        md:p-4
                      "
                    >
                      <div
                        className="
                          mb-3

                          font-customFont1
                          text-[10px]
                          font-semibold
                          uppercase
                          tracking-[0.12em]

                          text-black/45

                          dark:text-white/45

                          md:text-xs
                          md:tracking-wide
                        "
                      >
                        Lisää hintasivuja alueittain
                      </div>

                      <div className="max-h-[220px] overflow-y-auto pr-1 md:max-h-[260px]">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

                          {/* KOKO SUOMI */}
                          <div>
                            <div
                              className="
                                mb-2

                                text-[10px]
                                font-semibold
                                uppercase
                                tracking-wide

                                text-black/45

                                dark:text-white/45

                                md:text-[11px]
                              "
                            >
                              Koko Suomi
                            </div>

                            <div className="flex flex-wrap justify-center gap-1.5 md:justify-start md:gap-2">
                              {LANDING_PRICE_LINKS_MORE.filter((link) =>
                                [
                                  "Duo juhliin",
                                  "Sooloartisti juhliin",
                                  "Taikuri juhliin",
                                ].includes(link.label)
                              ).map((link) => (
                                <a
                                  key={link.href}
                                  href={link.href}
                                  className="
                                    rounded-full

                                    border
                                    border-black/10

                                    bg-white/90

                                    px-2.5
                                    py-1.5

                                    font-customFont1
                                    text-[10px]
                                    font-semibold

                                    text-black/70

                                    shadow-sm

                                    transition

                                    active:scale-[0.98]

                                    md:px-3
                                    md:text-xs

                                    md:hover:-translate-y-0.5
                                    md:hover:bg-white
                                    md:hover:text-black
                                    md:hover:shadow-md

                                    dark:border-white/10
                                    dark:bg-white/10
                                    dark:text-white/75
                                    dark:hover:bg-white/15
                                    dark:hover:text-white
                                  "
                                >
                                  {link.label}
                                </a>
                              ))}
                            </div>
                          </div>

                          {/* UUSIMAA */}
                          <div>
                            <div
                              className="
                                mb-2

                                text-[10px]
                                font-semibold
                                uppercase
                                tracking-wide

                                text-black/45

                                dark:text-white/45

                                md:text-[11px]
                              "
                            >
                              Uusimaa
                            </div>

                            <div className="flex flex-wrap justify-center gap-1.5 md:justify-start md:gap-2">
                              {LANDING_PRICE_LINKS_MORE.filter((link) =>
                                link.label.includes("Uusimaa") ||
                                link.label.includes("Helsinki") ||
                                link.label.includes("Espoo") ||
                                link.label.includes("Vantaa") ||
                                link.label.includes("Kerava") ||
                                link.label.includes("Järvenpää") ||
                                link.label.includes("Tuusula")
                              ).map((link) => (
                                <a
                                  key={link.href}
                                  href={link.href}
                                  className="
                                    rounded-full

                                    border
                                    border-black/10

                                    bg-white/90

                                    px-2.5
                                    py-1.5

                                    font-customFont1
                                    text-[10px]
                                    font-semibold

                                    text-black/70

                                    shadow-sm

                                    transition

                                    active:scale-[0.98]

                                    md:px-3
                                    md:text-xs

                                    md:hover:-translate-y-0.5
                                    md:hover:bg-white
                                    md:hover:text-black
                                    md:hover:shadow-md

                                    dark:border-white/10
                                    dark:bg-white/10
                                    dark:text-white/75
                                    dark:hover:bg-white/15
                                    dark:hover:text-white
                                  "
                                >
                                  {link.label}
                                </a>
                              ))}
                            </div>
                          </div>

                          {/* MUUT MAAKUNNAT */}
                          <div>
                            <div
                              className="
                                mb-2

                                text-[10px]
                                font-semibold
                                uppercase
                                tracking-wide

                                text-black/45

                                dark:text-white/45

                                md:text-[11px]
                              "
                            >
                              Muut maakunnat
                            </div>

                            <div className="flex flex-wrap justify-center gap-1.5 md:justify-start md:gap-2">
                              {LANDING_PRICE_LINKS_MORE.filter((link) =>
                                ![
                                  "Duo juhliin",
                                  "Sooloartisti juhliin",
                                  "Taikuri juhliin",
                                ].includes(link.label) &&
                                !link.label.includes("Uusimaa") &&
                                !link.label.includes("Helsinki") &&
                                !link.label.includes("Espoo") &&
                                !link.label.includes("Vantaa") &&
                                !link.label.includes("Kerava") &&
                                !link.label.includes("Järvenpää") &&
                                !link.label.includes("Tuusula")
                              ).map((link) => (
                                <a
                                  key={link.href}
                                  href={link.href}
                                  className="
                                    rounded-full

                                    border
                                    border-black/10

                                    bg-white/90

                                    px-2.5
                                    py-1.5

                                    font-customFont1
                                    text-[10px]
                                    font-semibold

                                    text-black/70

                                    shadow-sm

                                    transition

                                    active:scale-[0.98]

                                    md:px-3
                                    md:text-xs

                                    md:hover:-translate-y-0.5
                                    md:hover:bg-white
                                    md:hover:text-black
                                    md:hover:shadow-md

                                    dark:border-white/10
                                    dark:bg-white/10
                                    dark:text-white/75
                                    dark:hover:bg-white/15
                                    dark:hover:text-white
                                  "
                                >
                                  {link.label}
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </nav>

                 {cardsSection}
              </div>

              <div className="h-4" />
              <div className="rounded-2xl md:p-6 bg-white/5 dark:bg-black/40 backdrop-blur-md shadow-lg ring-1 ring-white/10">
                {showDeferredHomeSections ? (
                <Suspense fallback={<LazySectionFallback />}>
                  <FeaturedProfileCard />
                  <BookingFlow />
                  <LatestArtists layout="split" />
                  <MonthlyFeatured />
                  <WhyBuukkeriSection />
                  <HomeFAQ />
                </Suspense>
                ) : (
                  <LazySectionFallback />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking modal (pikahaku) */}
      {showBooking && (
        <Suspense fallback={null}>
          <BookingModal
            open={showBooking}
            onClose={closeBooking}
            onApply={handleApplyFromModal}
            artistTypes={artistTypesUI}
            genres={genresUI}
            services={SHOW_COMPANIES ? availableServices : []}
            initialDate={selectedDate || todayLocalISO()}
            initialLocation={locationFilter}
            initialRegions={selectedRegions}
            isWelcome={false}
            regions={regionsUI}
          />
        </Suspense>
      )}
    </>
  );
}